import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe, faRobot, faComments, faCheckCircle, faSpinner,
  faChevronRight, faChevronDown, faMagic, faLock, faSearch,
  faEnvelope, faKey, faBuilding, faArrowRight, faTimes,
  faBars, faCircle, faCheck, faClock, faExclamationCircle,
  faUser, faSitemap, faClipboardList, faLayerGroup,
  faHandshake, faShoppingCart, faGraduationCap, faCode,
  faBriefcase, faInfoCircle, faArrowLeft, faEye, faEyeSlash,
  faPhone, faSquareCheck, faSquare, faList, faMoon, faSun, faCog,
  faHome, faTableColumns, faPlus
} from '@fortawesome/free-solid-svg-icons';
import MessageRenderer from '../../components/chat/MessageRenderer';
import { BOT_PURPOSE_PROMPTS } from '../../utils/bot_prompts_client';
import { BOT_PERSONALITIES } from '../../utils/bot_personalities';
import { toast } from 'react-toastify';
import { createClient, getApiRootUrl } from '../../services/api';
import '../../styles/messageResponse.css';
import '../../components/chat/FeedbackButtons.css';
import { generateUUID } from '../../utils/uuid';
import feedbackService from '../../services/feedbackService';
import QASessionPreview from '../../components/chat/QASessionPreview';
import { WebsitePreview } from '../../components/chat/WebsitePreview';

interface Message {
  role: 'assistant' | 'user' | 'system';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  isThinking?: boolean;
  isLoading?: boolean;
  isStreaming?: boolean;
  options?: OptionButton[];
  pageList?: PageInfo[];
  crawlProgress?: CrawlProgress;
  scanProgress?: ScanProgress;
  scanComplete?: ScanComplete;
  qaCards?: QACard[];
  botRecommendation?: {
    type?: string;
    reason: string;
    recommended?: string;
    score?: number;
    allOptions?: any[];
  };
  messageTag?: string;
  id?: string;
  feedback?: 'thumbs_up' | 'thumbs_down' | 'need_review';
  feedbackReason?: string;
  metadata?: {
    conversation_id?: string;
  };
  isKnowledgeResponse?: boolean; // True for responses from knowledge base
}

interface OptionButton {
  id: string;
  label: string;
  icon?: any;
  description?: string;
  disabled?: boolean;
  action: () => void;
}

interface PageInfo {
  url: string;
  title: string;
  selected: boolean;
  disabled?: boolean;
  category?: string;
}

interface CrawlProgress {
  status: 'crawling' | 'embedding' | 'extracting_contacts' | 'extracting_branding' | 'generating_themes' | 'qa_generation' | 'completed';
  progress: number;
  message: string;
  pagesProcessed?: number;
  totalPages?: number;
  visitedUrls?: string[];  // Real-time discovered pages from stream
}

interface QACard {
  question: string;
  answer: string;
  source: string;
  id?: string;
  feedback?: 'thumbs_up' | 'thumbs_down' | 'need_review';
  feedbackReason?: string;
}

interface DiscoveredPage {
  url: string;
  title: string;
  category: string;
  selected: boolean;
}

interface ScanProgress {
  status: 'discovering' | 'analyzing' | 'complete' | 'categorizing';
  progress: number;
  message: string;
  pagesDiscovered?: DiscoveredPage[];
  categories?: Record<string, number>;
}

const getCategoryDisplayName = (name: string): string => {
  if (!name) {
    return 'Uncategorized';
  }
  const parts = name.split('>').map(part => part.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : name.trim();
};

type ScanCategoryAggregate = {
  name: string;
  count: number;
  icon?: string;
  rawNames: string[];
  listingCount?: number;
  listingChildrenCount?: number;
  sampleProductUrls?: string[];
};

const aggregateScanCategories = (categories: any[]): ScanCategoryAggregate[] => {
  const map = new Map<string, ScanCategoryAggregate>();

  categories.forEach(cat => {
    const displayName = getCategoryDisplayName(cat?.name);
    const existing = map.get(displayName);
    const detailCount =
      typeof cat?.detail_count === 'number'
        ? cat.detail_count
        : typeof cat?.count === 'number'
          ? cat.count
          : 0;
    const listingCount =
      typeof cat?.listing_count === 'number' ? cat.listing_count : 0;
    const listingChildrenCount =
      typeof cat?.listing_children_count === 'number' ? cat.listing_children_count : 0;
    const sampleProducts: string[] = Array.isArray(cat?.sample_product_urls)
      ? cat.sample_product_urls.filter((url: unknown): url is string => typeof url === 'string')
      : [];

    if (existing) {
      existing.count += detailCount;
      existing.listingCount = (existing.listingCount || 0) + listingCount;
      existing.listingChildrenCount =
        (existing.listingChildrenCount || 0) + listingChildrenCount;
      if (cat?.icon && !existing.icon) {
        existing.icon = cat.icon;
      }
      if (cat?.name && !existing.rawNames.includes(cat.name)) {
        existing.rawNames.push(cat.name);
      }
      if (sampleProducts.length) {
        const seen = new Set(existing.sampleProductUrls || []);
        sampleProducts.forEach(url => {
          if (!seen.has(url)) {
            seen.add(url);
            existing.sampleProductUrls = [...(existing.sampleProductUrls || []), url];
          }
        });
      }
    } else {
      map.set(displayName, {
        name: displayName,
        count: detailCount,
        icon: cat?.icon,
        rawNames: cat?.name ? [cat.name] : [],
        listingCount: listingCount || undefined,
        listingChildrenCount: listingChildrenCount || undefined,
        sampleProductUrls: sampleProducts.length ? [...sampleProducts] : undefined
      });
    }
  });

  return Array.from(map.values())
    .map(cat => ({
      ...cat,
      icon: cat.icon || '📄'
    }))
    .sort((a, b) => b.count - a.count);
};

interface ScanComplete {
  totalPages: number;
  totalFromSitemap?: number;
  totalAnalyzed?: number;
  categories: Array<{ category: string; count: number; icon: string; rawCategory?: string }>;
  siteType?: string;
  analysis?: string;
  pagesDiscovered?: DiscoveredPage[];
}

// Conversation state interface for perfect isolation
interface ConversationState {
  // Chat state
  messages: Message[];
  conversationHistory: Array<{role: string; content: string}>;
  conversationId: string | null;
  
  // Client/Project state  
  clientDisplayName: string;
  realClientId: string | null;
  websiteUrl: string;
  clientDetails: {
    name: string;
    email: string;
    phone: string;
    company_name: string;
    password: string;
  };
  
  // Bot configuration
  botPurpose: string;
  botPersonality: string;
  
  // Website analysis state  
  selectedPages: string[]; // Changed from Set to Array for JSON serialization
  preScannedPages: PageInfo[];
  qaCards: QACard[];
  qaSessions: Array<{
    session_id: string;
    qa_pairs: any[];
    created_at: string;
    sources: string[];
  }>;
  sessionId: string | null;
  activeSessionId: string | null;
  
  // Flow state
  currentStep: 'website' | 'scanning' | 'bot-type' | 'personality' | 'page-selection' | 'client-details' | 'crawling' | 'demo';
  collectingField: string | null;
  
  // UI state that should persist
  waitingForInput: boolean;
  setupProgress: number;
  setupPhaseLabel: string;
}

// Extended conversation interface with complete state
interface ConversationData {
  id: string;
  clientId: string | null;
  title: string;
  messages: Message[];
  created_at: string;
  websiteUrl?: string;
  clientName?: string;
  // Complete conversation state
  state?: ConversationState;
}

// Helper functions
const extractTitleFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    if (path === '/' || path === '') return 'Home';
    
    // Remove leading/trailing slashes and file extensions
    const cleanPath = path.replace(/^\/|\/$/g, '').replace(/\.[^/.]+$/, '');
    
    // Convert URL path to title
    return cleanPath
      .split(/[-_/]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } catch {
    return 'Page';
  }
};

const categorizeUrl = (url: string): string => {
  const lowerUrl = url.toLowerCase();
  
  // Check URL patterns for categorization
  if (lowerUrl.includes('/support') || lowerUrl.includes('/help') || lowerUrl.includes('/faq') || lowerUrl.includes('/contact')) {
    return 'support';
  }
  if (lowerUrl.includes('/doc') || lowerUrl.includes('/guide') || lowerUrl.includes('/tutorial') || lowerUrl.includes('/learn')) {
    return 'documentation';
  }
  if (lowerUrl.includes('/product') || lowerUrl.includes('/feature') || lowerUrl.includes('/pricing') || lowerUrl.includes('/plan')) {
    return 'product';
  }
  if (lowerUrl.includes('/about') || lowerUrl.includes('/team') || lowerUrl.includes('/career') || lowerUrl.includes('/company')) {
    return 'company';
  }
  if (lowerUrl.includes('/api') || lowerUrl.includes('/developer') || lowerUrl.includes('/sdk')) {
    return 'technical';
  }
  if (lowerUrl.includes('/privacy') || lowerUrl.includes('/terms') || lowerUrl.includes('/legal')) {
    return 'legal';
  }
  if (lowerUrl.includes('/blog') || lowerUrl.includes('/news') || lowerUrl.includes('/article')) {
    return 'blog';
  }
  if (lowerUrl.includes('/shop') || lowerUrl.includes('/store') || lowerUrl.includes('/cart')) {
    return 'commerce';
  }
  
  return 'general';
};

const getCategoryIcon = (category: string): string => {
  const icons: Record<string, string> = {
    support: '🛟',
    documentation: '📚',
    product: '📦',
    company: '🏢',
    technical: '⚙️',
    legal: '⚖️',
    blog: '📝',
    commerce: '🛒',
    general: '📄'
  };
  return icons[category] || '📄';
};

const suggestBotTypeBasedOnContent = (categories: Record<string, number>): { type: string; reason: string } | null => {
  const total = Object.values(categories).reduce((sum, count) => sum + count, 0);
  
  // Calculate percentages
  const percentages: Record<string, number> = {};
  Object.entries(categories).forEach(([cat, count]) => {
    percentages[cat] = (count / total) * 100;
  });
  
  // Suggest based on content distribution.
  if (categories.support > 3 || percentages.support > 20) {
    return { type: 'Customer Support', reason: 'Your website has multiple support and help pages' };
  }
  
  if (categories.documentation > 5 || percentages.documentation > 30) {
    return { type: 'Educational Bot', reason: 'You have extensive documentation and guides' };
  }
  
  if (categories.commerce > 0) {
    return { type: 'Sales Assistant', reason: 'E-commerce functionality detected on your site' };
  }
  
  if (categories.technical > 3) {
    return { type: 'Technical Support', reason: 'API and developer resources found' };
  }
  
  // Default to general if no clear pattern
  return null;
};


const OnboardingDemo: React.FC = () => {
  const navigate = useNavigate();
  const userRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const isConsultantOrSuper = userRole === 'consultant' || userRole === 'super';
  
  // Theme state
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  // Chat State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [messageQueue, setMessageQueue] = useState<Array<{content: string, isAssistant: boolean, metadata?: any}>>([]);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  
  // Add a ref to prevent double clicks on options
  const optionClickedRef = useRef(false);
  
  // Use ref to store pages immediately
  const pagesRef = useRef<PageInfo[]>([]);
  const pageSelectionTimerRef = useRef<any>(null);
  
  // Onboarding Flow State
  const [currentStep, setCurrentStep] = useState<'website' | 'scanning' | 'bot-type' | 'personality' | 'page-selection' | 'client-details' | 'crawling' | 'demo'>('website');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [botPurpose, setBotPurpose] = useState('');
  const [botType, setBotType] = useState<string | null>(null); // Store the actual bot type for API
  const [botPersonality, setBotPersonality] = useState('professional');
  const [botRecommendations, setBotRecommendations] = useState<any[]>([]); // Store bot recommendations for filtering
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const selectedPagesRef = useRef<Set<string>>(new Set());
  const [preScannedPages, setPreScannedPages] = useState<PageInfo[]>([]);
  const [pageSearchTerm, setPageSearchTerm] = useState('');
  const [brandingMessageShown, setBrandingMessageShown] = useState(false);
  const [extractedBranding, setExtractedBranding] = useState<any>(null);
  const handleCrawlingCompleteRunningRef = useRef(false);
  const extractedBrandingRef = useRef(extractedBranding);
  const currentStepRef = useRef(currentStep);
  const brandingMessageShownRef = useRef(brandingMessageShown);
  
  // Client Details State
  const [clientDetails, setClientDetails] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    password: ''
  });
  const [realClientId, setRealClientId] = useState<string | null>(null);

  // Demo detection - available throughout the component
  const isDemoMode = realClientId && (realClientId.includes('demo') || realClientId.includes('temp') || realClientId.includes('test') || realClientId.includes('preview') || (realClientId.length >= 36 && realClientId.includes('-')));
const maxPagesForDemo = 10;

// Debug demo mode
console.log('Demo mode check - realClientId:', realClientId, 'isDemoMode:', isDemoMode, 'length:', realClientId?.length, 'includes dash:', realClientId?.includes('-'));

useEffect(() => {
  extractedBrandingRef.current = extractedBranding;
}, [extractedBranding]);

useEffect(() => {
  currentStepRef.current = currentStep;
}, [currentStep]);

useEffect(() => {
  brandingMessageShownRef.current = brandingMessageShown;
}, [brandingMessageShown]);

// Demo State
const [sessionId, setSessionId] = useState<string | null>(null);
  const [qaCards, setQaCards] = useState<QACard[]>([]);
  // Removed demo message count - unlimited demo
  
  // Theme Customization State
  const [themeVariants, setThemeVariants] = useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);
  const [isFetchingVariants, setIsFetchingVariants] = useState(false);
  const [qaSessions, setQaSessions] = useState<Array<{
    session_id: string;
    qa_pairs: any[];
    created_at: string;
    sources: string[];
  }>>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string; content: string}>>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [clientDisplayName, setClientDisplayName] = useState<string>('');
  const [ecommerceAssistantEnabled, setEcommerceAssistantEnabled] = useState(false);

  // Modify extractedBranding to include interfaceVariant when e-commerce assistant is enabled
  const enhancedBranding = React.useMemo(() => {
    if (!extractedBranding) return extractedBranding;

    return {
      ...extractedBranding,
      interfaceVariant: ecommerceAssistantEnabled ? 'ecommerce' : (extractedBranding.interfaceVariant || 'current'),
      selectedTriggers: ecommerceAssistantEnabled ? ['searchBarTrigger'] : (extractedBranding.selectedTriggers || ['floatingAvatar']),
      ecommerceBannerTrigger: ecommerceAssistantEnabled ? {
        enabled: true,
        position: 'bottom-right',
        bgColor: '#007bff',
        iconColor: '#ffffff',
        textColor: '#ffffff',
        pulseAnimation: true,
        size: 'medium',
        shape: 'circle',
        text: 'E-commerce Assistant',
        showText: false,
        tooltipText: 'Start voice-powered shopping assistant',
        icon: 'microphone'
      } : undefined,
      ecommerceResultsOverlay: ecommerceAssistantEnabled ? {
        bgColor: '#ffffff',
        textColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: '12px',
        backdropBlur: false,
        maxWidth: '400px',
        maxHeight: '600px',
        animation: 'slide-up',
        productCardBg: '#ffffff',
        productCardBorder: '#e5e7eb',
        productCardShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        productImageSize: '80px',
        productSpacing: '12px'
      } : undefined
    };
  }, [extractedBranding, ecommerceAssistantEnabled]);

  // Signup Form State
  // Removed signup form - unlimited demo
  // Removed signup-related state variables
  const [passwordVisible, setPasswordVisible] = useState(false);
  
  // Conversational collection state
  const [collectingField, setCollectingField] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showWebsitePreview, setShowWebsitePreview] = useState(false);
  const [activeQASessionId, setActiveQASessionId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [hasCreatedInitialConversation, setHasCreatedInitialConversation] = useState(false);
  const [setupProgress, setSetupProgress] = useState<number>(0);
  const [setupPhaseLabel, setSetupPhaseLabel] = useState<string>('');
  const [showFeedbackReasonFor, setShowFeedbackReasonFor] = useState<string | null>(null);
  const MAX_SAVED_MESSAGES = 10;

const FEEDBACK_OPTIONS = [
  { value: 'incomplete_answer', label: 'Incomplete answer' },
  { value: 'wrong_answer', label: 'Wrong answer' },
  { value: 'wrong_citation', label: 'Wrong citation/source' },
  { value: 'no_response', label: 'No response' },
  { value: 'response_broken', label: 'Response broken/cut off' },
  { value: 'irrelevant_response', label: 'Irrelevant response' },
  { value: 'hallucination', label: 'Hallucination/made up info' },
  { value: 'poor_context', label: 'Poor context understanding' },
  { value: 'technical_error', label: 'Technical error' }
];

  // Conversation State Management Functions
  const createInitialConversationState = (): ConversationState => ({
    messages: [],
    conversationHistory: [],
    conversationId: null,
    clientDisplayName: '',
    realClientId: null,
    websiteUrl: '',
    clientDetails: {
      name: '',
      email: '',
      phone: '',
      company_name: '',
      password: ''
    },
    botPurpose: '',
    botPersonality: '',
    selectedPages: [],
    preScannedPages: [],
    qaCards: [],
    qaSessions: [],
    sessionId: null,
    activeSessionId: null,
    currentStep: 'website',
    collectingField: null,
    waitingForInput: false,
    setupProgress: 0,
    setupPhaseLabel: ''
  });

  const getCurrentConversationState = (): ConversationState => ({
    messages,
    conversationHistory,
    conversationId,
    clientDisplayName,
    realClientId,
    websiteUrl,
    clientDetails,
    botPurpose,
    botPersonality,
    selectedPages: Array.from(selectedPages),
    preScannedPages,
    qaCards,
    qaSessions,
    sessionId,
    activeSessionId,
    currentStep,
    collectingField,
    waitingForInput,
    setupProgress,
    setupPhaseLabel
  });

  const restoreConversationState = (state: ConversationState) => {
    setMessages(state.messages);
    setConversationHistory(state.conversationHistory);
    setConversationId(state.conversationId);
    setClientDisplayName(state.clientDisplayName);
    setRealClientId(state.realClientId);
    setWebsiteUrl(state.websiteUrl);
    setClientDetails(state.clientDetails);
    setBotPurpose(state.botPurpose);
    setBotPersonality(state.botPersonality);
    setSelectedPages(new Set(state.selectedPages));
    setPreScannedPages(state.preScannedPages);
    setQaCards(state.qaCards);
    setQaSessions(state.qaSessions);
    setSessionId(state.sessionId);
    setActiveSessionId(state.activeSessionId);
    setCurrentStep(state.currentStep);
    setCollectingField(state.collectingField);
    setWaitingForInput(state.waitingForInput);
    setSetupProgress(state.setupProgress);
    setSetupPhaseLabel(state.setupPhaseLabel);
  };

  const saveCurrentConversationState = () => {
    if (!activeConversationId) return;
    
    const currentState = getCurrentConversationState();
    setConversations(prev => prev.map(conv => 
      conv.id === activeConversationId 
        ? { 
            ...conv, 
            messages: currentState.messages,
            title: currentState.clientDisplayName || currentState.clientDetails.company_name || conv.title,
            clientName: currentState.clientDisplayName || currentState.clientDetails.company_name || '',
            clientId: currentState.realClientId,
            websiteUrl: currentState.websiteUrl,
            state: currentState
          }
        : conv
    ));
  };

  // Load saved conversations on mount (localStorage only per requirement)
  // Normalize IDs to sequential numeric strings: "0", "1", ... so tabs are ordered
  useEffect(() => {
    try {
      const raw = localStorage.getItem('demo_conversations');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Sort by created_at ascending if available, otherwise keep original order
          const sorted = [...parsed].sort((a, b) => {
            const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return at - bt;
          });

          // Reassign ids to sequential numeric strings
          const normalized = sorted.map((conv: any, idx: number) => ({
            ...conv,
            id: String(idx)
          }));

          setConversations(normalized);
          // Persist normalized list back to localStorage
          localStorage.setItem('demo_conversations', JSON.stringify(normalized));
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, []);

  // Persist conversations to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('demo_conversations', JSON.stringify(conversations));
    } catch {}
  }, [conversations]);

  // Auto-save current conversation
  const saveCurrentConversation = useCallback(() => {
    const firstUserMsg = messages.find(m => m.role === 'user' && m.content?.trim());
    if (!firstUserMsg && messages.length <= 1) return; // Don't save if only welcome message
    
    const trimmed = messages.slice(-MAX_SAVED_MESSAGES);
    
    if (activeConversationId) {
      // Update existing conversation
      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          // Only update clientName if this is the initial setup conversation with Q&A
          const isInitialSetup = messages.some(m => m.qaCards);
          return {
            ...conv,
            messages: trimmed,
            clientName: isInitialSetup ? (clientDisplayName || conv.clientName) : conv.clientName,
            websiteUrl: websiteUrl || conv.websiteUrl,
            clientId: realClientId || conv.clientId
          };
        }
        return conv;
      }));
    }
    // Removed auto-creation of new conversations - only update existing ones
  }, [messages, activeConversationId, clientDisplayName, websiteUrl, realClientId, clientDetails.name]);

  // Auto-save conversation state when important changes happen
  useEffect(() => {
    if (activeConversationId) {
      // Save complete conversation state
      saveCurrentConversationState();
    }
  }, [
    messages, conversationHistory, clientDisplayName, realClientId, 
    websiteUrl, clientDetails, botPurpose, botPersonality, selectedPages,
    qaCards, qaSessions, currentStep, collectingField, waitingForInput,
    activeConversationId
  ]);

  // Periodic auto-save for active conversations
  useEffect(() => {
    if (!activeConversationId) return;
    
    const interval = setInterval(() => {
      const hasContent = messages.length > 1 || 
                        conversationHistory.length > 0 || 
                        websiteUrl || 
                        clientDetails.name || 
                        qaCards.length > 0;
      
      if (hasContent) {
        saveCurrentConversationState();
      }
    }, 10000); // Save every 10 seconds
    
    return () => clearInterval(interval);
  }, [activeConversationId]);

  // Progress Tracking for Header
  const stepProgress = {
    'website': 0,
    'scanning': 20,
    'bot-type': 35,
    'page-selection': 50,
    'client-details': 65,
    'crawling': 80,
    'demo': 100
  };

  // Auto-scroll to bottom
  useEffect(() => {
    // Skip if we're just showing feedback reasons (this prevents scroll on feedback click)
    if (showFeedbackReasonFor) return;
    
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]); // Trigger on any message updates

  // Initialize session
  useEffect(() => {
    initializeSession();
  }, []); // Empty dependency array ensures this only runs once

  // Fetch existing theme variants when realClientId is available
  useEffect(() => {
    if (realClientId && !themeVariants.length && !isGeneratingVariants) {
      console.log('Fetching existing theme variants for client:', realClientId);
      fetchThemeVariants(realClientId);
    }
  }, [realClientId, themeVariants.length, isGeneratingVariants]);

  useEffect(() => {
    selectedPagesRef.current = selectedPages;
  }, [selectedPages]);

  const initializeSession = async () => {
    // Prevent multiple initializations
    if (sessionId || messages.length > 0) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/session`, {
        method: 'POST'
      });
      const data = await response.json();
      setSessionId(data.session_id);
      
      // Initial greeting - only add if no messages exist
      if (messages.length === 0) {
        setTimeout(() => {
          addAssistantMessage(
            "👋 Hi! I'm here to show you how our AI assistant can transform your website into an intelligent helper for your visitors.\n\nLet's start by analyzing your website. What's your URL?",
            true
          );
          setWaitingForInput(true);
        }, 500);
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
      toast.error('Failed to start demo. Please refresh the page.');
    }
  };


  const addAssistantMessage = (content: string, withTyping: boolean = true, extras?: Partial<Message>) => {
    // Special handling for crawl-analyzing messages: check ALL messages by messageTag
    if (extras?.messageTag === 'crawl-analyzing') {
      const existingCrawlMessage = messages.find(m => m.role === 'assistant' && m.messageTag === 'crawl-analyzing');
      if (existingCrawlMessage) {
        console.log('Skipping duplicate crawl-analyzing message');
        return; // Always prevent duplicate crawl-analyzing messages
      }
    }
    
    // Check for duplicate messages in the last 3 messages - more lenient matching
    const recentMessages = messages.slice(-3);
    const isDuplicate = recentMessages.some(m => {
      if (!m.content) return false;

      // Check if the content is very similar (90% match)
      const currentWords = content.toLowerCase().split(/\s+/);
      const existingWords = m.content.toLowerCase().split(/\s+/);

      if (currentWords.length === 0 || existingWords.length === 0) return false;

      const commonWords = currentWords.filter(word =>
        existingWords.some(existingWord =>
          existingWord.includes(word) || word.includes(existingWord)
        )
      );

      const similarity = commonWords.length / Math.max(currentWords.length, existingWords.length);
      return similarity > 0.8; // 80% similarity threshold
    });

    if (isDuplicate) {
      console.log('Skipping duplicate message:', content.substring(0, 50) + '...');
      return;
    }

    const message: Message = {
      role: 'assistant',
      content: withTyping ? '' : content,
      timestamp: new Date(),
      isTyping: withTyping,
      id: generateUUID(),
      ...extras
    };

    setMessages(prev => [...prev, message]);

    if (withTyping) {
      // Simple typing effect
      let index = 0;
      const interval = setInterval(() => {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.isTyping) {
            lastMessage.content = content.substring(0, index);
            if (index >= content.length) {
              lastMessage.isTyping = false;
            }
          }
          return newMessages;
        });
        index += 3;
        if (index >= content.length) {
          clearInterval(interval);
        }
      }, 30);
    }

    return message;
  };

  const addUserMessage = (content: string) => {
    setMessages(prev => [...prev, {
      role: 'user',
      content,
      timestamp: new Date(),
      id: generateUUID()
    }]);
  };

  const addSystemMessage = (content: string) => {
    setMessages(prev => [...prev, {
      role: 'system',
      content,
      timestamp: new Date()
    }]);
  };

  const handleUserInput = async () => {
    if (!inputValue.trim() || isProcessing || !waitingForInput) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setWaitingForInput(false);
    addUserMessage(userMessage);

    // Handle based on current step
    switch (currentStep) {
      case 'website':
        await handleWebsiteInput(userMessage);
        break;
      case 'client-details':
        await handleClientDetailsInput(userMessage);
        break;
      case 'demo':
        await handleDemoChat(userMessage);
        break;
      default:
        break;
    }
  };
  
  const handleClientDetailsInput = async (input: string) => {
    // Parse the input to see if user provided multiple fields at once
    const parsedData = parseUserInput(input);
    
    // Update client details with parsed data
    if (parsedData.email) clientDetails.email = parsedData.email;
    if (parsedData.name) clientDetails.name = parsedData.name;
    if (parsedData.phone) clientDetails.phone = parsedData.phone;
    if (parsedData.bot_name) clientDetails.company_name = parsedData.bot_name;
    
    // Handle based on what we're currently collecting
    if (collectingField === 'bot_name') {
      clientDetails.company_name = parsedData.bot_name || input;
      
      // Ask for email next
      if (!clientDetails.email) {
        addAssistantMessage(
          `Perfect! I'll call it "${clientDetails.company_name}".\n\nWhat's your email address? This is where you'll receive your login details.`,
          true
        );
        setCollectingField('email');
        setWaitingForInput(true);
      } else {
        checkNextRequiredField();
      }
    } else if (collectingField === 'email') {
      if (!parsedData.email || !input.includes('@')) {
        addAssistantMessage("That doesn't look like a valid email. Please enter your email address.", true);
        setWaitingForInput(true);
        return;
      }
      
      clientDetails.email = parsedData.email || input;
      
      // Ask for name next
      if (!clientDetails.name) {
        addAssistantMessage(
          `Got it! Email set to ${clientDetails.email}.\n\nWhat's your name?`,
          true
        );
        setCollectingField('name');
        setWaitingForInput(true);
      } else {
        checkNextRequiredField();
      }
    } else if (collectingField === 'name') {
      clientDetails.name = parsedData.name || input;
      
      // Ask for phone
      if (!clientDetails.phone) {
        addAssistantMessage(
          `Nice to meet you, ${clientDetails.name}!\n\nWhat's your phone number?`,
          true
        );
        setCollectingField('phone');
        setWaitingForInput(true);
      } else {
        checkNextRequiredField();
      }
    } else if (collectingField === 'phone') {
      clientDetails.phone = parsedData.phone || input;
      
      // Generate a secure password
      clientDetails.password = generateSecurePassword();
      
      addAssistantMessage(
        `Perfect! I have all the information I need:\n\n` +
        `🤖 Bot Name: ${clientDetails.company_name}\n` +
        `📧 Email: ${clientDetails.email}\n` +
        `👤 Name: ${clientDetails.name}\n` +
        `📱 Phone: ${clientDetails.phone}\n\n` +
        `I'll generate a secure password and email it to you. Ready to create your AI assistant?`,
        true,
        {
          options: [
            {
              id: 'create',
              label: '🚀 Yes, Create Knowledgebase!',
              action: () => {
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage && lastMessage.options) {
                    delete lastMessage.options;
                  }
                  return newMessages;
                });
                addUserMessage("Yes, create Knowledgebase!");
                handleClientDetailsSubmit();
              }
            },
            {
              id: 'edit',
              label: '✏️ Let me edit something',
              action: () => {
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage && lastMessage.options) {
                    delete lastMessage.options;
                  }
                  return newMessages;
                });
                addUserMessage("Let me edit");
                showEditOptions();
              }
            }
          ]
        }
      );
    }
  };
  
  const parseUserInput = (input: string): any => {
    const result: any = {};
    
    // Check for email
    const emailMatch = input.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) result.email = emailMatch[0];
    
    // Check for phone
    const phoneMatch = input.match(/[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,3}[)]?[-\s\.]?[0-9]{3,4}[-\s\.]?[0-9]{3,6}/);
    if (phoneMatch) result.phone = phoneMatch[0];
    
    // Check for bot name patterns
    if (input.toLowerCase().includes('bot name:') || input.toLowerCase().includes('bot:')) {
      const botMatch = input.match(/bot\s*(?:name)?:\s*([^,\n]+)/i);
      if (botMatch) result.bot_name = botMatch[1].trim();
    } else if (!result.email && !result.phone && collectingField === 'bot_name') {
      result.bot_name = input;
    }
    
    // Check for name patterns
    if (input.toLowerCase().includes('name:') && !input.toLowerCase().includes('bot')) {
      const nameMatch = input.match(/name:\s*([^,\n]+)/i);
      if (nameMatch) result.name = nameMatch[1].trim();
    } else if (!result.email && !result.phone && !result.bot_name && collectingField === 'name') {
      result.name = input;
    }
    
    return result;
  };
  
  const checkNextRequiredField = () => {
    if (!clientDetails.company_name) {
      setCollectingField('bot_name');
      addAssistantMessage(
        "What would you like to call your bot? For example: '" + suggestBotName(websiteUrl) + "'",
        true
      );
      setWaitingForInput(true);
    } else if (!clientDetails.email) {
      setCollectingField('email');
      addAssistantMessage("What's your email address?", true);
      setWaitingForInput(true);
    } else if (!clientDetails.name) {
      setCollectingField('name');
      addAssistantMessage("What's your name?", true);
      setWaitingForInput(true);
    } else if (!clientDetails.phone) {
      setCollectingField('phone');
      addAssistantMessage("What's your phone number?", true);
      setWaitingForInput(true);
    } else {
      // All fields collected, proceed
      clientDetails.password = generateSecurePassword();
      handleClientDetailsSubmit();
    }
  };
  
  const generateSecurePassword = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };
  
  const loadQASession = (session: any) => {
    setActiveSessionId(session.session_id);
    setQaCards(session.qa_pairs);
    
    // Clear messages and show the loaded Q&A
    setMessages([]);
    
    addAssistantMessage(
      `Loading Q&A Session from ${new Date(session.created_at).toLocaleString()}`,
      false
    );
    
    setTimeout(() => {
      addAssistantMessage(
        "Here are the Q&A pairs from this session:",
        true,
        { qaCards: session.qa_pairs }
      );
      
      setTimeout(() => {
        addAssistantMessage(
          "You can ask me any question about your business and I'll answer based on your website content!",
          true
        );
        setWaitingForInput(true);
        setCurrentStep('demo');
      }, 1500);
    }, 1000);
  };
  
  const showEditOptions = () => {
    addAssistantMessage(
      "What would you like to edit?",
      true,
      {
        options: [
          {
            id: 'cancel_edit',
            label: '↩️ Cancel and go back',
            action: () => {
              addUserMessage('Cancel edit');
              // Clear any pending input state
              setCollectingField(null);
              setWaitingForInput(false);
              // Re-show the final confirmation card with create/edit options
              addAssistantMessage(
                `Perfect! I have all the information I need:\n\n` +
                `🤖 Bot Name: ${clientDetails.company_name}\n` +
                `📧 Email: ${clientDetails.email}\n` +
                `👤 Name: ${clientDetails.name}\n` +
                `📱 Phone: ${clientDetails.phone}\n\n` +
                `I'll generate a secure password and email it to you. Ready to create your AI assistant?`,
                true,
                {
                  options: [
                    {
                      id: 'create_back',
                      label: '🚀 Yes, Create Knowledgebase!',
                      action: () => {
                        setMessages(prev => {
                          const newMessages = [...prev];
                          const lastMessage = newMessages[newMessages.length - 1];
                          if (lastMessage && lastMessage.options) delete lastMessage.options;
                          return newMessages;
                        });
                        addUserMessage('Yes, create Knowledgebase!');
                        handleClientDetailsSubmit();
                      }
                    },
                    {
                      id: 'edit_back',
                      label: '✏️ Let me edit something',
                      action: () => {
                        setMessages(prev => {
                          const newMessages = [...prev];
                          const lastMessage = newMessages[newMessages.length - 1];
                          if (lastMessage && lastMessage.options) delete lastMessage.options;
                          return newMessages;
                        });
                        addUserMessage('Let me edit');
                        showEditOptions();
                      }
                    }
                  ]
                }
              );
            }
          },
          {
            id: 'bot_name',
            label: '🤖 Bot Name',
            action: () => {
              setCollectingField('bot_name');
              addUserMessage("Change bot name");
              addAssistantMessage(`Current bot name is "${clientDetails.company_name}". What would you like to change it to?`, true);
              setWaitingForInput(true);
            }
          },
          {
            id: 'email',
            label: '📧 Email',
            action: () => {
              setCollectingField('email');
              addUserMessage("Change email");
              addAssistantMessage(`Current email is ${clientDetails.email}. What's your new email?`, true);
              setWaitingForInput(true);
            }
          },
          {
            id: 'name',
            label: '👤 Name',
            action: () => {
              setCollectingField('name');
              addUserMessage("Change name");
              addAssistantMessage(`Current name is ${clientDetails.name}. What's your name?`, true);
              setWaitingForInput(true);
            }
          },
          {
            id: 'phone',
            label: '📱 Phone',
            action: () => {
              setCollectingField('phone');
              addUserMessage("Change phone");
              addAssistantMessage(`Current phone is ${clientDetails.phone}. What's your new phone number?`, true);
              setWaitingForInput(true);
            }
          }
        ]
      }
    );
  };

  const handleWebsiteInput = async (url: string) => {
    // Validate URL
    if (!url.includes('.')) {
      addAssistantMessage("That doesn't look like a valid website URL. Please enter your website address (e.g., example.com)");
      setWaitingForInput(true);
      return;
    }

    // Normalize URL
    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
      setWebsiteUrl(normalizedUrl);
      setIsProcessing(true);
      optionClickedRef.current = false;
      setCurrentStep('scanning');

      // Reset branding message state for new demo
      setBrandingMessageShown(false);
      
      // Clear any existing messages with options to prevent confusion
      setMessages(prev => prev.map(msg => ({
        ...msg,
        options: undefined,
        pageList: undefined
      })));

    // Start scanning
    addAssistantMessage("Perfect! Let me scan your website to see what we're working with... 🔍");
    
    // Add initial scanning message
    const scanningMessage: Message = {
      role: 'system',
      content: '',
      timestamp: new Date(),
      scanProgress: {
        status: 'discovering',
        progress: 0,
        message: `Discovering pages on ${url}...`,
        pagesDiscovered: []
      }
    };
    setMessages(prev => [...prev, scanningMessage]);

    try {
      // PHASE 1: Get pages quickly without categorization
      const quickResponse = await fetch(`${getApiRootUrl()}/api/demo-enhanced/demo-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: normalizedUrl,
          bot_purpose: 'general',  // Get all pages for scanning
          quick_response: true  // Get pages immediately without categorization
        })
      });

      if (!quickResponse.ok) {
        throw new Error('Failed to scan website');
      }

      const quickData = await quickResponse.json();
      const allPagesFromBackend = quickData.preview_pages || [];

      // Check if no pages were found
      if (allPagesFromBackend.length === 0) {
        throw new Error('no-pages-found');
      }

      // DEMO MODE: Only use first 10 pages for everything
      const allPages = allPagesFromBackend.slice(0, 10);
      console.log(`📊 Demo mode: Using ${allPages.length} pages out of ${allPagesFromBackend.length} returned by backend`);

      // Store counts for accurate display
      const totalFromSitemap = quickData.total_discovered_from_sitemap || allPagesFromBackend.length;
      const totalAnalyzed = quickData.total_analyzed || 0;

      // Initialize with basic categories (will be updated after categorization)
      const categories: Record<string, number> = {};

      // Show discovered pages immediately (without categories initially)
      let discoveredPages: DiscoveredPage[] = [];

      for (let i = 0; i < allPages.length; i++) {
        const page = allPages[i];

        // Add page to discovered list (category is 'pending' initially)
        discoveredPages.push({
          url: page.url,
          title: page.title || extractTitleFromUrl(page.url),
          category: page.category === 'pending' ? 'Uncategorized' : (page.category || 'General'),
          selected: true
        });

        // Update scanning progress to show discovered pages
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.scanProgress) {
            lastMessage.scanProgress = {
              status: 'discovering',
              progress: Math.round(((i + 1) / allPages.length) * 50), // 50% for discovery
              message: `Found ${i + 1} pages...`,
              pagesDiscovered: [...discoveredPages],
              categories: { ...categories }
            };
          }
          return newMessages;
        });

        // Add small delay for visual effect
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Update message to show categorization is starting
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.scanProgress) {
          lastMessage.scanProgress = {
            status: 'categorizing',
            progress: 50,
            message: `Categorizing ${allPages.length} pages with AI...`,
            pagesDiscovered: [...discoveredPages],
            categories: { ...categories }
          };
        }
        return newMessages;
      });

      // PHASE 2: Now categorize the discovered URLs
      let botRecommendations: any[] = [];
      let recommendedBot = 'General Assistant';
      let categories_detail: any[] = [];

      // Only categorize if we haven't already (quick_response means no categorization yet)
      if (quickData.categorization_status === 'pending') {
        const urlList = allPages.map((p: any) => p.url);

        try {
          const categorizeResponse = await fetch(`${getApiRootUrl()}/api/demo-enhanced/categorize-urls`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              urls: urlList,
              base_url: normalizedUrl
            })
          });

          if (categorizeResponse.ok) {
            const categorizeData = await categorizeResponse.json();

            // Update discovered pages with proper categories
            const urlToCategoryMap = new Map<string, string>();
            categorizeData.categorized_pages?.forEach((cp: any) => {
              urlToCategoryMap.set(cp.url, cp.category);
            });

            discoveredPages = discoveredPages.map(page => ({
              ...page,
              category: urlToCategoryMap.get(page.url) || page.category
            }));

            // Update categories count
            categories_detail = categorizeData.categories || [];
            categories_detail.forEach((cat: any) => {
              categories[cat.name] = cat.count;
            });

            // Store LLM analysis for later use
            botRecommendations = categorizeData.bot_recommendations?.all_bot_types || [];
            recommendedBot = categorizeData.bot_recommendations?.recommended || 'General Assistant';

            // Save to state for access during client creation
            setBotRecommendations(botRecommendations);

            // Update progress to show categorization complete
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.scanProgress) {
                lastMessage.scanProgress = {
                  status: 'discovering',
                  progress: 100,
                  message: `Categorization complete! Found ${discoveredPages.length} pages`,
                  pagesDiscovered: [...discoveredPages],
                  categories: { ...categories }
                };
              }
              return newMessages;
            });

            console.log('🤖 Bot recommendations from categorization:', {
              all_bot_types: botRecommendations,
              recommended: recommendedBot,
              count: botRecommendations.length
            });
          }
        } catch (categorizationError) {
          console.warn('Categorization failed, continuing with uncategorized pages:', categorizationError);
        }
      }
      
      // Remove scanning message and add completion message
      setMessages(prev => prev.filter(m => !m.scanProgress));
      
      // Prepare final pages with better data
      const normalizeUrl = (value: string) => value.replace(/\/$/, '').toLowerCase();
      const baseHomeUrl = normalizeUrl(websiteUrl);

      // Simple and robust homepage detection
      const isHomePage = (url: string) => {
        const normalized = normalizeUrl(url);
        const patterns = [
          baseHomeUrl,
          baseHomeUrl + '/',
          baseHomeUrl + '/index.html',
          baseHomeUrl + '/index.php',
          baseHomeUrl + '/home',
        ];

        return patterns.some(pattern => normalized === pattern);
      };

        const finalPages = discoveredPages
          .slice()
          .sort((a, b) => {
            const aIsHome = isHomePage(a.url);
            const bIsHome = isHomePage(b.url);

            // Debug: Log homepage detection
            if (aIsHome || bIsHome) {
              console.log(`Homepage sorting: ${a.url} (home: ${aIsHome}) vs ${b.url} (home: ${bIsHome})`);
            }

            // Homepage always comes first
            if (aIsHome && !bIsHome) return -1;
            if (!aIsHome && bIsHome) return 1;
            return 0;
          })
          .map(p => {
            const isHome = isHomePage(p.url);

            return {
              url: p.url,
              title: p.title,
              selected: isHome, // Pre-select homepage (always true)
              disabled: isHome // Make homepage selection mandatory and unchangeable
            };
          });

      // Verify homepage is at the top
      const homepageIndex = finalPages.findIndex(p => p.disabled);
      if (homepageIndex > 0) {
        console.log('WARNING: Homepage not at top! Index:', homepageIndex, 'URL:', finalPages[homepageIndex]?.url);
      } else if (homepageIndex === 0) {
        console.log('✅ Homepage correctly positioned at top:', finalPages[0]?.url);
      }
      
      console.log('Total pages prepared:', finalPages.length, 'pages (already limited to 10)');

      // Store all pages (already limited to 10)
      pagesRef.current = finalPages;
      setPreScannedPages(finalPages);

      // Auto-select all pages (they're already limited to 10)
      const defaultSelection = new Set<string>(finalPages.map(p => p.url));
      setSelectedPages(defaultSelection);
      selectedPagesRef.current = defaultSelection;
      console.log('Auto-selected all', defaultSelection.size, 'pages for demo');
      
      // Show completion message with LLM categories
      console.log('📊 Categories from backend:', categories_detail);
      const aggregatedCategories = aggregateScanCategories(categories_detail);
      const categoryList = aggregatedCategories.map((cat) => ({
        category: cat.name,
        rawCategory: cat.rawNames[0] || cat.name,
        count: cat.count,
        icon: cat.icon || getCategoryIcon(cat.rawNames[0] || cat.name)
      }));
      console.log('📊 Processed category list:', categoryList);

      // Add completion message with category breakdown and discovered pages
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'scan-complete',
        timestamp: new Date(),
        scanComplete: {
          totalPages: discoveredPages.length,
          totalFromSitemap: totalFromSitemap,
          totalAnalyzed: discoveredPages.length,
          categories: categoryList,
          siteType: quickData.site_type || 'general',
          analysis: quickData.analysis || '',
          pagesDiscovered: discoveredPages // Include discovered pages for UI display
        }
      }]);

      // Show LLM bot recommendation with all options
      if (botRecommendations.length > 0) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'system',
            content: 'bot-recommendation',
            timestamp: new Date(),
            botRecommendation: {
              recommended: recommendedBot,
              score: 100,
              allOptions: botRecommendations,
              reason: `Based on website analysis`
            }
          }]);
        }, 500);
      }

      // Move to bot type selection with LLM recommendations
      setTimeout(() => {
        handleBotTypeSelection(finalPages, botRecommendations, recommendedBot);
      }, 1500);

    } catch (error: any) {
      console.error('Error scanning website:', error);
      
      // Remove scanning message
      setMessages(prev => prev.filter(m => !m.scanProgress));
      
      // Check if no pages were found
      if (error.message === 'no-pages-found') {
        addAssistantMessage(
          `❌ I couldn't find any pages on ${normalizedUrl}\n\n` +
          `This could happen if:\n` +
          `• The website is not accessible\n` +
          `• The domain doesn't exist\n` +
          `• The website blocks automated scanning\n\n` +
          `Please check your website URL and try again.`
        );
        
        // Clear any messages with options to prevent confusion
        setMessages(prev => prev.map(msg => ({
          ...msg,
          options: undefined,
          pageList: undefined
        })));
        
        // Allow user to try again
        setCurrentStep('website');
        setIsProcessing(false);
        optionClickedRef.current = false;
        setWaitingForInput(true);
        setCollectingField('website');
        
        setTimeout(() => {
          addAssistantMessage("Please enter a valid website URL to continue:", true);
        }, 1500);
        
      } else {
        // Generic error handling
        addAssistantMessage(
          `❌ I encountered an error while scanning your website.\n\n` +
          `Error: ${error.message || 'Failed to scan website'}\n\n` +
          `Please try again with a different URL.`
        );
        
        // Clear any messages with options to prevent confusion
        setMessages(prev => prev.map(msg => ({
          ...msg,
          options: undefined,
          pageList: undefined
        })));
        
        // Allow user to try again
        setCurrentStep('website');
        setIsProcessing(false);
        optionClickedRef.current = false;
        setWaitingForInput(true);
        setCollectingField('website');
        
        setTimeout(() => {
          addAssistantMessage("Please enter your website URL to continue:", true);
        }, 1500);
      }
    }
  };

  const normalizeBotType = (label: string) => {
    if (!label) return 'general';
    const l = label.toLowerCase();
    if (l.includes('e-commerce') || l.includes('ecommerce') || l.includes('shopping')) return 'ecommerce';
    if (l.includes('support')) return 'support';
    if (l.includes('sales')) return 'sales';
    if (l.includes('educat')) return 'educational';
    if (l.includes('company')) return 'company';
    if (l.includes('technical') || l.includes('developer')) return 'technical';
    return 'general';
  };

  const handleBotTypeSelection = (scannedPages?: any[], llmBotTypes?: any[], recommendedBot?: string) => {
    setCurrentStep('bot-type');
    setIsProcessing(false);

    // If pages were passed, ensure they're stored
    if (scannedPages && scannedPages.length > 0) {
      console.log('Bot selection received pages:', scannedPages.length);
      setPreScannedPages(scannedPages);
      // DON'T reset selection - preserve the 10 pages that were auto-selected during scanning
      // The selectedPagesRef.current already has the correct 10 pages
      console.log('Preserving existing selection:', selectedPagesRef.current.size, 'pages');
    }

    // Build bot options from LLM recommendations if available
    let botOptions: OptionButton[] = [];

    console.log('🤖 handleBotTypeSelection - llmBotTypes:', llmBotTypes, 'count:', llmBotTypes?.length);

    if (llmBotTypes && llmBotTypes.length > 0) {
      // Use LLM-generated bot types
      botOptions = llmBotTypes.map((bot: any) => {
        // For demo, limit General Assistant to 10 pages max
        const urlCount = bot.bot_type === 'General Assistant'
          ? Math.min(bot.total_urls || 10, 10)
          : bot.total_urls || 0;

        // Build description with URL count
        let description = bot.description || '';
        if (urlCount > 0) {
          description = `${description}${description ? ' - ' : ''}Covers ${urlCount} page${urlCount !== 1 ? 's' : ''}`;
        }
        if (bot.score && bot.bot_type !== 'General Assistant') {
          description = `${description} (${bot.score}% match)`;
        }

        return {
          id: bot.bot_type.toLowerCase().replace(/ /g, '-'),
          label: `${bot.icon} ${bot.bot_type}${bot.bot_type === recommendedBot ? ' ⭐ (Recommended)' : ''}`,
          description: description,
          action: () => selectBotType(normalizeBotType(bot.bot_type), bot.bot_type)
        };
      });
      console.log('🤖 Created bot options from LLM:', botOptions.length);
    } else {
      console.log('🤖 No LLM bot types, using fallback default bot types');
      // Fallback to default bot types
      botOptions = [
        {
          id: 'general',
          label: '🤖 General Assistant',
          description: 'Answers all types of questions',
          action: () => selectBotType('general', 'General Assistant')
        },
        {
          id: 'support',
          label: '🛟 Customer Support',
          description: 'Focused on helping customers',
          action: () => selectBotType('support', 'Customer Support')
        },
        {
          id: 'sales',
          label: '💼 Sales Assistant',
          description: 'Converts visitors to customers',
          action: () => selectBotType('sales', 'Sales Assistant')
        },
        {
          id: 'educational',
          label: '📚 Educational Bot',
          description: 'Teaching and training focused',
          action: () => selectBotType('educational', 'Educational Bot')
        }
      ];
    }

    addAssistantMessage(
      "Great! Now, what type of AI assistant would work best for your business? Choose the personality that matches your needs:",
      true,
      { options: botOptions }
    );
  };

  const selectBotType = (type: string, displayLabel?: string) => {
    const purposeKey = (type in BOT_PURPOSE_PROMPTS ? type : 'general') as keyof typeof BOT_PURPOSE_PROMPTS;
    const promptData = BOT_PURPOSE_PROMPTS[purposeKey];

    setBotPurpose(purposeKey);
    setBotType(type); // Store the actual bot type for API
    
    // Set e-commerce variant when e-commerce bot type is selected
    const isEcommerce = !!(type.toLowerCase().includes('ecommerce') || type.toLowerCase().includes('commerce') || displayLabel?.toLowerCase().includes('ecommerce') || displayLabel?.toLowerCase().includes('commerce'));
    setEcommerceAssistantEnabled(isEcommerce);
    
    setIsProcessing(true);

    const purposeDescription = promptData?.description || '';
    const purposeExamples = promptData?.examples || [];

    
    // Update last message to show selection
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.options) {
        delete lastMessage.options;
      }
      return newMessages;
    });

    addUserMessage(`Selected: ${displayLabel || promptData?.name || (purposeKey.charAt(0).toUpperCase() + purposeKey.slice(1))}`);

    if (purposeDescription) {
      addAssistantMessage(
        `${promptData?.name || 'Selected bot'} will focus on ${purposeDescription.toLowerCase()}.` +
        (purposeExamples.length ? ` I'll prioritize content such as ${purposeExamples.slice(0, 3).join(', ')}.` : ''),
        true
      );
    }

    // Move to personality selection
    setTimeout(() => {
      handlePersonalitySelection();
    }, 500);
  };
  
  const handlePersonalitySelection = () => {
    setCurrentStep('personality');
    setIsProcessing(false);
    
    const personalityOptions: OptionButton[] = Object.entries(BOT_PERSONALITIES).map(([key, personality]) => ({
      id: key,
      label: `${personality.icon ? '🎭 ' : ''}${personality.name}`,
      description: personality.description,
      icon: personality.icon,
      action: () => selectPersonality(key)
    }));
    
    addAssistantMessage(
      "Great choice! Now let's define your AI's personality. How should it interact with your visitors?",
      false,
      { options: personalityOptions }
    );
  };
  
  const selectPersonality = (personality: string) => {
    setBotPersonality(personality);
    setIsProcessing(true);
    
    // Update last message to show selection
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.options) {
        delete lastMessage.options;
      }
      return newMessages;
    });

    addUserMessage(`Selected: ${BOT_PERSONALITIES[personality].name} personality`);

    // Get the current pages from state or ref
    const currentPages = preScannedPages.length > 0 ? preScannedPages : pagesRef.current;
    console.log('selectPersonality - passing pages:', currentPages?.length);
    
    if (pageSelectionTimerRef.current) {
      clearTimeout(pageSelectionTimerRef.current);
      pageSelectionTimerRef.current = null;
    }
    pageSelectionTimerRef.current = window.setTimeout(() => {
      handlePageSelection(currentPages);
      pageSelectionTimerRef.current = null;
    }, 500);
  };

  const handlePageSelection = (passedPages?: any[]) => {
    // Prevent multiple calls - check if we're already in page-selection step
    if (currentStep === 'page-selection') {
      console.log('handlePageSelection - already in page-selection step, skipping');
      return;
    }

    setCurrentStep('page-selection');
    setIsProcessing(false);
    optionClickedRef.current = false;

    // Use passed pages if available, otherwise try state or ref
    const pagesToUse = passedPages || preScannedPages || pagesRef.current || ([] as PageInfo[]);
    console.log('handlePageSelection - using pages:', pagesToUse.length, 'passedPages:', passedPages?.length);

    // Ensure pages are set in state (but preserve existing selection)
    if (passedPages && passedPages.length > 0 && preScannedPages.length === 0) {
      setPreScannedPages(passedPages);
      // DON'T reset selection - we already have 10 pages auto-selected from scanning
      console.log('Pages set, preserving existing selection:', selectedPagesRef.current.size, 'pages');
    }

    // Demo mode variables are now available at component level
    const effectivePagesToUse = isDemoMode ? Math.min(pagesToUse.length, maxPagesForDemo) : pagesToUse.length;
    const isLimitedByDemo = isDemoMode && pagesToUse.length > maxPagesForDemo;

    // Check if this message was already added recently
    const recentMessages = messages.slice(-3);
    const hasRecentPageMessage = recentMessages.some(m =>
      m.content && m.content.includes('pages on your website')
    );

    if (!hasRecentPageMessage) {

    const pageSelectionOptions: OptionButton[] = [
      {
        id: 'select-all',
        label: isLimitedByDemo ? `✅ Use All ${effectivePagesToUse} Pages (Demo)` : '✅ Use All Pages',
        description: isLimitedByDemo
          ? `Demo mode: Include all ${effectivePagesToUse} pages for optimal performance`
          : `Include all ${pagesToUse.length} pages`,
        disabled: true, // Will be enabled after message is shown
        action: () => {
          console.log('Use all pages clicked - currentStep:', currentStep);
          if (pageSelectionTimerRef.current) {
            clearTimeout(pageSelectionTimerRef.current);
            pageSelectionTimerRef.current = null;
          }
          // Force the step and then confirm; cast avoids TS union literal mismatch
          setCurrentStep('page-selection' as any);
          setTimeout(() => confirmPageSelection(true), 0);
        }
      },
      {
        id: 'customize',
        label: '🎯 Let Me Choose',
        description: isLimitedByDemo
          ? `Select from ${effectivePagesToUse} available pages`
          : 'Select specific pages',
        disabled: true, // Will be enabled after message is shown
        action: () => showPageList()
      }
    ];

    const demoLimitMessage = isLimitedByDemo
      ? ` Demo mode allows up to ${maxPagesForDemo} pages for optimal performance.`
      : '';

      const pageSelectionMessage = `Perfect choice! I've selected the top ${pagesToUse.length} pages from your website for demo mode. Would you like to use all ${pagesToUse.length} pages to train your AI, or would you prefer to select specific ones?`;

      addAssistantMessage(
        pageSelectionMessage,
        true,
        { options: pageSelectionOptions }
      );

      // Calculate typing duration based on message length (3 chars per 30ms + buffer)
      const typingDuration = Math.ceil(pageSelectionMessage.length / 3) * 30 + 300;

      // Enable the buttons after the message finishes typing
      setTimeout(() => {
        setMessages(prev => prev.map((msg, idx) => {
          if (idx === prev.length - 1 && msg.options) {
            return {
              ...msg,
              options: msg.options.map(opt => ({ ...opt, disabled: false }))
            };
          }
          return msg;
        }));
      }, typingDuration);
    }
  };

  const showPageList = () => {
    console.log('showPageList called - currentStep:', currentStep, 'selectedPages:', selectedPages.size);
    if (pageSelectionTimerRef.current) {
      clearTimeout(pageSelectionTimerRef.current);
      pageSelectionTimerRef.current = null;
    }

    // Remove options from last message
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.options) {
        delete lastMessage.options;
      }
      return newMessages;
    });

    addUserMessage("Let me choose specific pages");

    // Use ref if state is empty
    const pagesToShow = preScannedPages.length > 0 ? preScannedPages : pagesRef.current || [];
    console.log('showPageList - pagesToShow:', pagesToShow.length, 'preScannedPages:', preScannedPages.length, 'pagesRef.current:', pagesRef.current?.length);

    // In demo mode, only show first 10 pages
    const pagesToDisplay = isDemoMode ? pagesToShow.slice(0, maxPagesForDemo) : pagesToShow;
    console.log('showPageList - isDemoMode:', isDemoMode, 'maxPagesForDemo:', maxPagesForDemo, 'pagesToDisplay:', pagesToDisplay.length);

    if (pagesToDisplay.length === 0) {
      console.error('showPageList - no pages to display!');
      toast.error('No pages available to select from');
      return;
    }

    // In demo mode, ensure all 10 pages are pre-selected (user can deselect if they want)
    if (isDemoMode && selectedPages.size === 0) {
      const allDisplayedUrls = new Set(pagesToDisplay.map(p => p.url));
      setSelectedPages(allDisplayedUrls);
      selectedPagesRef.current = allDisplayedUrls;
      console.log('✅ Auto-selected all 10 displayed pages for demo mode');
    }

    const pageList = pagesToDisplay.map((page, idx) => ({
      ...page,
      selected: selectedPages.has(page.url), // Preserve existing selection state
      disabled: false // All pages in the list are selectable
    }));

    const selectOptions: OptionButton[] = [
      {
        id: 'toggle-all',
        label: '☑ Select All',
        action: () => toggleAllPages()
      },
      {
        id: 'confirm',
        label: '✓ Confirm Selection',
        description: `${selectedPages.size} pages selected`,
        action: () => confirmPageSelection(false)
      }
    ];

    const demoLimitMessage = isDemoMode ?
      ` Demo mode: Homepage is pre-selected and required. You can select up to ${maxPagesForDemo - 1} additional pages.` :
      '';
    addAssistantMessage(
      `Here are all the pages I found. Click on any page to toggle its selection:${demoLimitMessage}`,
      false,
      { pageList, options: selectOptions }
    );
  };

  const togglePageSelection = (url: string) => {
    console.log('togglePageSelection called with url:', url, 'current selection size:', selectedPages.size);

    // Find the page to check if it's the homepage
    const pagesToUse = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
    const page = pagesToUse.find(p => p.url === url);

    // If trying to unselect homepage, prevent it
    if (page?.disabled) {
      console.log('togglePageSelection - cannot unselect homepage:', url);
      toast.info('Homepage selection cannot be changed - it is required');
      return;
    }

    const newSelection = new Set(selectedPagesRef.current);
    const isCurrentlySelected = newSelection.has(url);

    // If trying to select more than 10 pages (excluding homepage), prevent it
    if (!isCurrentlySelected && newSelection.size >= maxPagesForDemo) {
      console.log('togglePageSelection - maximum pages reached:', maxPagesForDemo);
      toast.warning(`Maximum ${maxPagesForDemo} pages allowed in demo mode`);
      return;
    }

    if (isCurrentlySelected) {
      newSelection.delete(url);
      console.log('togglePageSelection - removed url:', url);
    } else {
      newSelection.add(url);
      console.log('togglePageSelection - added url:', url);
    }
    console.log('togglePageSelection - new selection size:', newSelection.size, 'urls:', Array.from(newSelection));
    setSelectedPages(newSelection);
    selectedPagesRef.current = newSelection;

    // Update the page list in the last message
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.pageList) {
        const pagesToUse = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
        lastMessage.pageList = pagesToUse.map(page => ({
          ...page,
          selected: newSelection.has(page.url)
        }));
        // Update the confirm button description
        if (lastMessage.options) {
          const confirmBtn = lastMessage.options.find(opt => opt.id === 'confirm');
          if (confirmBtn) {
            confirmBtn.description = `${newSelection.size} pages selected`;
          }
        }
      }
      return [...newMessages];
    });
  };

  const toggleAllPages = () => {
    const pagesToUse = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
    let newSelection: Set<string>;

    if (isDemoMode) {
      // In demo mode: select all available pages (already limited to 10 by backend)
      const pagesToSelect = pagesToUse.slice(0, maxPagesForDemo);
      newSelection = new Set<string>(pagesToSelect.map(p => p.url));

      console.log('toggleAllPages - demo mode: selecting all available pages, new size:', newSelection.size);
      setSelectedPages(newSelection);
      selectedPagesRef.current = newSelection;
    } else {
      // Regular mode: select/deselect all pages
      const currentSelectionSize = selectedPagesRef.current.size;
      newSelection = currentSelectionSize === pagesToUse.length ? new Set<string>() : new Set(pagesToUse.map(p => p.url));
      console.log('toggleAllPages - regular mode: current:', currentSelectionSize, 'total:', pagesToUse.length, 'new size:', newSelection.size);
      setSelectedPages(newSelection);
      selectedPagesRef.current = newSelection;
    }

    updateMessageSelection(newSelection);
  };

  // Helper function to update message selection display
  const updateMessageSelection = (selection: Set<string>) => {
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.pageList) {
        const pagesToUse = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
        lastMessage.pageList = pagesToUse.map(page => ({
          ...page,
          selected: selection.has(page.url)
        }));
        if (lastMessage.options) {
          // Update toggle button
          const toggleBtn = lastMessage.options.find(opt => opt.id === 'toggle-all');
          if (toggleBtn) {
            const totalPages = preScannedPages.length > 0 ? preScannedPages.length : pagesRef.current?.length || 0;
            toggleBtn.label = selection.size === totalPages ? '☐ Deselect All' : (isDemoMode ? '✅ Select Homepage + Top 9' : '☑ Select All');
          }
          // Update confirm button
          const confirmBtn = lastMessage.options.find(opt => opt.id === 'confirm');
          if (confirmBtn) {
            confirmBtn.description = `${selection.size} pages selected`;
          }
        }
      }
      return [...newMessages];
    });
  };

  const confirmPageSelection = (allPages: boolean) => {
    console.log('confirmPageSelection called with:', { allPages, selectedPagesSize: selectedPages.size, selectedPagesArray: Array.from(selectedPages) });
    console.log('confirmPageSelection - currentStep:', currentStep, 'isProcessing:', isProcessing);
    if (pageSelectionTimerRef.current) {
      clearTimeout(pageSelectionTimerRef.current);
      pageSelectionTimerRef.current = null;
    }

    // Prevent multiple clicks - check if already processing or moved to next step
    if (isProcessing || optionClickedRef.current) {
      console.log('confirmPageSelection - ignoring click, already processing or not in page-selection step');
      return;
    }

    // Immediately set processing flags to prevent double clicks
    setIsProcessing(true);
    optionClickedRef.current = true;

    // Snapshot selection before any state changes for accurate logging & messaging
    const selectionSnapshot = Array.from(selectedPages);

    // Get the pages to use - same logic as in handlePageSelection
    const pagesToUseInConfirm = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
    console.log('confirmPageSelection - pages available:', pagesToUseInConfirm.length, 'isDemoMode:', isDemoMode);

    // If allPages is true, set the selected pages
    if (allPages) {
      let pagesToSelect: PageInfo[];

      if (isDemoMode) {
        // In demo mode: select all available pages (already limited to 10 by backend)
        // The backend already sliced to 10 pages in demo mode, so just use them all
        pagesToSelect = pagesToUseInConfirm.slice(0, maxPagesForDemo);
      } else {
        pagesToSelect = pagesToUseInConfirm; // All pages in regular mode
      }

      const newSelection = new Set<string>(pagesToSelect.map((p: PageInfo) => p.url));
      setSelectedPages(newSelection);
      selectedPagesRef.current = newSelection;
      console.log(`Setting selected pages for allPages=true: ${newSelection.size} pages`);
    } else {
      // For manual selection, validate that at least one page is selected
      if (selectionSnapshot.length === 0) {
        console.log('No pages selected, showing error');
        toast.error('Please select at least one page');
        return;
      }
      console.log('Manual selection confirmed with', selectionSnapshot.length, 'pages');
    }

    // Use the selection that was already set above (homepage + 9 others for demo mode)
    // No need to recreate finalSelection - it's already correct in selectedPagesRef.current
    const finalSelection = Array.from(selectedPagesRef.current);
    console.log('✅ Final selection for demo:', finalSelection.length, 'pages');

    // Verify it's limited to 10 pages in demo mode
    if (isDemoMode && finalSelection.length > maxPagesForDemo) {
      console.error('❌ ERROR: Selection exceeds demo limit!', finalSelection.length, '>', maxPagesForDemo);
      // Force limit to 10
      const limitedSelection = finalSelection.slice(0, maxPagesForDemo);
      const limitedSet = new Set(limitedSelection);
      setSelectedPages(limitedSet);
      selectedPagesRef.current = limitedSet;
      console.log('✅ Corrected to', limitedSelection.length, 'pages');
    }

    // Remove options from last message
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.options) {
        delete lastMessage.options;
        delete lastMessage.pageList;
      }
      return newMessages;
    });

    addUserMessage(allPages ? "Yes, use all 10 pages for my AI!" : `Selected ${finalSelection.length} pages`);

    console.log('About to transition to client-details step...');

    // Move to client details collection with a small delay to ensure state updates complete
    setTimeout(() => {
      console.log('Transitioning to client-details step...');
      console.log('Calling showClientDetailsForm...');
      showClientDetailsForm();
    }, 100);
  };

  const showClientDetailsForm = () => {
    console.log('showClientDetailsForm called');
    // Reset processing state when we reach the next step
    setIsProcessing(false);
    optionClickedRef.current = false;
    setCurrentStep('client-details');
    
    // Prepare the questions we need to ask
    const requiredFields = {
      name: clientDetails.name,
      email: clientDetails.email,
      phone: clientDetails.phone,
      bot_name: clientDetails.company_name || extractBotNameFromUrl(websiteUrl),
      password: clientDetails.password
    };
    
    // Check what we're missing
    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value || value === '')
      .map(([key]) => key);
    
    if (missingFields.length === 0) {
      // We have all fields, proceed directly
      handleClientDetailsSubmit();
      return;
    }
    
    // Start conversational collection
    addAssistantMessage(
      "Great! I'm almost ready to create your AI assistant. Just need a few quick details from you.\n\nWhat would you like to call your bot? For example: '" + 
      suggestBotName(websiteUrl) + "' or '" + extractDomainName(websiteUrl) + " Assistant'",
      true
    );
    
    // Store what we need to collect
    setCollectingField('bot_name');
    setWaitingForInput(true);
  };
  
  // Fetch existing theme variants
  const fetchThemeVariants = async (clientId: string) => {
    console.log('fetchThemeVariants called with clientId:', clientId);
    setIsFetchingVariants(true);
    try {
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/select-theme-variant/${clientId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Theme variants response:', data);
        if (data.success && data.variants && data.variants.length > 0) {
          console.log('Fetched existing theme variants:', data.variants.length);
          console.log('Variant properties:', Object.keys(data.variants[0]?.mapping || {}));
          setThemeVariants(data.variants);
          setSelectedVariant(0);
          return true;
        } else if (data.success && (data.count === 0 || !data.variants || data.variants.length === 0)) {
          console.log('No theme variants found in backend, count:', data.count);
          return false;
        }
      } else {
        console.log('Failed to fetch theme variants:', response.status, response.statusText);
      }
      return false;
    } catch (error) {
      console.error('Error fetching theme variants:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error('Error details:', {
        message: errorMessage,
        stack: errorStack,
        clientId,
        API_BASE_URL: getApiRootUrl()
      });
      return false;
    } finally {
      setIsFetchingVariants(false);
    }
  };

  // Generate theme variants from extracted branding
  const generateThemeVariants = async (clientId: string) => {
    console.log('generateThemeVariants called with clientId:', clientId);
    setIsGeneratingVariants(true);
    try {
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/generate-theme-variants/${clientId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Theme variants response:', data);
        if (data.variants && data.variants.length > 0) {
          setThemeVariants(data.variants);
          setSelectedVariant(0);
          console.log('Generated theme variants:', data.variants);
          console.log('Theme variants state will be set to:', data.variants);
          
          // Apply the first variant by default
          await applyThemeVariant(0);
        } else {
          console.error('No variants in response:', data);
        }
      } else {
        console.error('Failed to generate theme variants:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error generating theme variants:', error);
    } finally {
      setIsGeneratingVariants(false);
    }
  };
  
  // Apply selected theme variant
  const applyThemeVariant = async (variantIndex: number) => {
    console.log('Applying theme variant:', variantIndex, 'realClientId:', realClientId, 'variant:', themeVariants[variantIndex]);

    if (!realClientId || !themeVariants[variantIndex]) {
      console.error('Cannot apply variant - missing data:', { realClientId, variantExists: !!themeVariants[variantIndex] });
      return;
    }

    try {
      // Immediately update selected variant for UI feedback
      setSelectedVariant(variantIndex);

      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/select-theme-variant/${realClientId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          variant_index: variantIndex
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Theme variant applied successfully, response:', data);

        setShowThemeSelector(false);

        const themePayload = data.theme_layout ? { mapping: data.theme_layout } : themeVariants[variantIndex];

        if (showWebsitePreview) {
          setTimeout(() => {
            window.postMessage({
              type: 'refresh-widget-theme',
              clientId: realClientId,
              themeVariant: themePayload
            }, '*');
            console.log('Sent theme refresh message to preview iframe');
          }, 300);
        }

        // Also refresh standalone widget in case preview is closed
        window.postMessage({
          type: 'refresh-widget-theme',
          clientId: realClientId,
          themeVariant: themePayload
        }, '*');

      } else {
        console.error('Failed to apply theme variant:', response.status);
        toast.error('Failed to apply theme');
        // Don't revert - keep UI in sync even if backend fails
      }
    } catch (error) {
      console.error('Error applying theme variant:', error);
      toast.error('Error applying theme');
      // Don't revert - keep UI in sync even if backend fails
    }
  };

  const extractBotNameFromUrl = (url: string): string => {
    try {
      const ensureProtocol = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
      const source = url && url.trim().length > 0 ? ensureProtocol(url) : getEffectiveNameSourceUrl();
      const domain = new URL(source).hostname.replace('www.', '');
      const name = domain.split('.')[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      return '';
    }
  };
  
  const suggestBotName = (url: string): string => {
    const base = extractBotNameFromUrl(url);
    return base ? `${base} AI` : 'Assistant AI';
  };
  
  const extractDomainName = (url: string): string => {
    try {
      const ensureProtocol = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
      const source = url && url.trim().length > 0 ? ensureProtocol(url) : getEffectiveNameSourceUrl();
      return new URL(source).hostname.replace('www.', '').split('.')[0];
    } catch {
      return 'Your';
    }
  };

  const getEffectiveNameSourceUrl = (): string => {
    const ensureProtocol = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
    if (websiteUrl && websiteUrl.trim().length > 0) return ensureProtocol(websiteUrl);
    const fallbackPages = preScannedPages.length > 0 ? preScannedPages : (pagesRef.current || ([] as PageInfo[]));
    if (fallbackPages.length > 0 && (fallbackPages[0] as any)?.url) {
      return ensureProtocol((fallbackPages[0] as any).url);
    }
    return '';
  };

  const handleClientDetailsSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    const { name, email, phone, company_name, password } = clientDetails;
    
    if (!name || !email || !phone || !company_name || !password) {
      toast.error('Missing required information');
      return;
    }
    
    if (password.length < 8) {
      clientDetails.password = generateSecurePassword();
    }
    
    setIsProcessing(true);
    setCollectingField(null);
    
    addAssistantMessage(
      `Setting up your account and ${clientDetails.company_name || 'AI assistant'}...`,
      true,
      { messageTag: 'setup-status', isLoading: true }
    );
    // Show pipeline: client creation start
    setMessages(prev => {
      const newMessages = [...prev];
      const progressMessage: Message = {
        role: 'system',
        content: '',
        timestamp: new Date(),
        crawlProgress: {
          status: 'crawling',
          progress: 5,
          message: 'Creating your account...',
          pagesProcessed: 0,
          totalPages: 0
        }
      };
      newMessages.push(progressMessage);
      return newMessages;
    });
    
    try {
      // Extract consultant_id from JWT (same as client form)
      const token = localStorage.getItem('auth_token');
      let consultantId: string | null = null;
      
      if (token) {
        try {
          const payloadPart = token.split('.')[1];
          const decoded = JSON.parse(atob(payloadPart));
          consultantId = decoded?.consultant_id || null;
          console.log('Extracted consultant_id from JWT:', consultantId);
        } catch (e) {
          console.error('Failed to decode JWT for consultant_id', e);
        }
      }
      
      // Get selected pages from ref (current selection state)
      let selectedPagesList = Array.from(selectedPagesRef.current);

      // Filter pages based on bot type if not General Assistant
      if (botType && botType !== 'general' && botRecommendations && botRecommendations.length > 0) {
        // Find the selected bot type's category names
        const selectedBot = botRecommendations.find((bot: any) =>
          normalizeBotType(bot.bot_type) === botType ||
          bot.bot_type.toLowerCase().replace(/ /g, '-') === botType
        );

        if (selectedBot) {
          // If bot has specific URLs, use those (most accurate)
          if (selectedBot.urls && selectedBot.urls.length > 0) {
            const botUrls = new Set(selectedBot.urls);
            const filteredPages = selectedPagesList.filter(pageUrl => botUrls.has(pageUrl));

            console.log(`Bot ${botType} has ${selectedBot.urls.length} specific URLs`);
            console.log(`Filtered to ${filteredPages.length} pages that match selected pages`);

            if (filteredPages.length > 0) {
              selectedPagesList = filteredPages;
            } else {
              console.log(`⚠️ No URLs matched, falling back to category matching`);
              // Fallback to category matching if no direct URL matches
              if (selectedBot.category_names && selectedBot.category_names.length > 0) {
                const allowedCategories = new Set(selectedBot.category_names.map((c: string) => c.toLowerCase()));
                const categoryFilteredPages = selectedPagesList.filter(pageUrl => {
                  const pageData = preScannedPages.find(p => p.url === pageUrl);
                  if (pageData && pageData.category) {
                    const pageCategory = pageData.category.toLowerCase();
                    const pageRoot = pageCategory.split(' > ')[0];
                    return allowedCategories.has(pageCategory) ||
                           allowedCategories.has(pageRoot) ||
                           Array.from(allowedCategories).some(cat => pageCategory.startsWith(cat + ' >'));
                  }
                  return false;
                });

                if (categoryFilteredPages.length > 0) {
                  selectedPagesList = categoryFilteredPages;
                  console.log(`✅ Category filtering: ${categoryFilteredPages.length} pages`);
                }
              }
            }
          }
          // If no URLs provided, fall back to category matching
          else if (selectedBot.category_names && selectedBot.category_names.length > 0) {
            console.log('No URLs in bot data, using category matching');
            const allowedCategories = new Set(selectedBot.category_names.map((c: string) => c.toLowerCase()));

            const filteredPages = selectedPagesList.filter(pageUrl => {
              const pageData = preScannedPages.find(p => p.url === pageUrl);
              if (pageData && pageData.category) {
                const pageCategory = pageData.category.toLowerCase();
                const pageRoot = pageCategory.split(' > ')[0];
                return allowedCategories.has(pageCategory) ||
                       allowedCategories.has(pageRoot) ||
                       Array.from(allowedCategories).some(cat => pageCategory.startsWith(cat + ' >'));
              }
              return false;
            });

            if (filteredPages.length > 0) {
              console.log(`✅ Filtered pages for ${botType}: ${filteredPages.length} pages`);
              selectedPagesList = filteredPages;
            }
          }
        }
      }

      console.log('Creating client with selected pages:', selectedPagesList.length, 'pages');

      // Create real client via the same API used by client-form
      console.log('Creating client with bot_type:', botType);
      const data = await createClient({
        name,
        prompt_display_name: company_name,
        email,
        phone,
        website: websiteUrl,
        password,
        bot_personality: botPersonality,
        bot_type: botType || undefined,
        selected_pages: selectedPagesList
      });
      if (!data || !data.client_id) {
        throw new Error('Failed to create client');
      }
      setRealClientId(data.client_id);
      // Set client display name for conversation labeling
      const clientName = clientDetails.company_name || clientDetails.name || 'Client';
      setClientDisplayName(clientName);
      
      // Update current conversation with client info immediately
      if (activeConversationId) {
        setConversations(prev => prev.map(conv => 
          conv.id === activeConversationId 
            ? { ...conv, clientName: clientName, clientId: data.client_id, websiteUrl: websiteUrl }
            : conv
        ));
      }
      
      // Debug log to check what was sent
      console.log('Selected pages sent to API:', Array.from(selectedPages));
      
      // Update pipeline: client created
      setMessages(prev => prev.map(m => m.crawlProgress ? {
        ...m,
        crawlProgress: { ...m.crawlProgress, progress: 10, message: 'Initializing scrape...' }
      } : m));

      // Create message without typing effect to avoid empty content showing "Processing..."
      // Content will be updated immediately by SSE stream
      addAssistantMessage("Account created successfully. Preparing to scrape your website...", false, { messageTag: 'crawl-analyzing', isLoading: true });
      
      // Mark setup-status message as complete (no longer loading) since crawl-analyzing has started
      setMessages(prev => prev.map(m => 
        m.role === 'assistant' && m.messageTag === 'setup-status'
          ? { ...m, isLoading: false }
          : m
      ));
      
      // Start real crawling - small delay to ensure message is in state before starting SSE stream
      setTimeout(() => {
        startRealCrawling(data.client_id);
      }, 100);
      
    } catch (error: any) {
      console.error('Error creating client:', error);
      setIsProcessing(false);

      // Remove the progress message since creation failed
      setMessages(prev => prev.filter(m => !m.crawlProgress));

      const errorMessage = error.message || 'Failed to create account';
      const suggestion = (error as any).suggestion;

      // Handle specific error cases
      if (errorMessage.toLowerCase().includes('email already registered') ||
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('already registered') ||
          errorMessage.toLowerCase().includes('duplicate')) {

        toast.error('Email already registered');

        // Use suggestion from backend if available
        const message = suggestion
          ? `The email **${clientDetails.email}** is already registered. ${suggestion}`
          : `The email **${clientDetails.email}** is already registered. Please use a different email address.`;

        addAssistantMessage(
          message,
          true,
          {
            options: [
              {
                id: 'change-email',
                label: '✉️ Use Different Email',
                action: () => {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.options) {
                      delete lastMessage.options;
                    }
                    return newMessages;
                  });
                  addUserMessage("Use different email");
                  setCollectingField('email');
                  addAssistantMessage(
                    `What email address would you like to use instead? (Current: ${clientDetails.email})`,
                    true
                  );
                  setWaitingForInput(true);
                }
              }
            ]
          }
        );
      } else {
        // Generic error handling for other cases
        toast.error(errorMessage);
        
        addAssistantMessage(
          `I encountered an issue: **${errorMessage}**\n\nWould you like to try again?`,
          true,
          {
            options: [
              {
                id: 'retry',
                label: '🔄 Try Again',
                action: () => {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.options) {
                      delete lastMessage.options;
                    }
                    return newMessages;
                  });
                  showClientDetailsForm();
                }
              },
              {
                id: 'cancel',
                label: 'Cancel',
                action: () => {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.options) {
                      delete lastMessage.options;
                    }
                    return newMessages;
                  });
                  addUserMessage("Cancel");
                  addAssistantMessage("No problem! Feel free to come back anytime.");
                }
              }
            ]
          }
        );
      }
    }
  };

  const startRealCrawling = async (clientId: string) => {
    setCurrentStep('crawling');
    setSetupProgress(80); // Start at 80% when crawling begins
    
    try {
      // Start crawling process
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/start-crawling/${clientId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to start crawling');
      }
      
      // Show real crawling progress
      streamCrawlingStatus(clientId);
      
    } catch (error) {
      console.error('Error starting crawl:', error);
      toast.error('Failed to start website processing');
      setIsProcessing(false);
    }
  };



  const streamCrawlingStatus = async (clientId: string) => {
    try {
      const eventSource = new EventSource(`${getApiRootUrl()}/api/demo-enhanced/stream-crawl-status/${clientId}`);
      let closedByClient = false;

      const closeStream = () => {
        if (!closedByClient) {
          closedByClient = true;
          eventSource.close();
        }
      };

      const timeoutId = window.setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          closeStream();
          toast.error('Processing is taking longer than expected');
          setIsProcessing(false);
        }
      }, 600000); // 10 minutes
      
      eventSource.onmessage = (event) => {
        try {
          const status = JSON.parse(event.data);
          console.log('Real-time crawl status:', status);
          
          if (status.error) {
            console.error('Status stream error:', status.error);
            closeStream();
            window.clearTimeout(timeoutId);
            setIsProcessing(false);
            return;
          }
          
          // Determine the current phase based on real status (for progress bar only)
          let progressStatus: CrawlProgress['status'] = 'crawling';
          let progressPercent = 0;
          
          if (status.status === 'initializing' || status.status === 'crawling_started') {
            progressStatus = 'crawling';
            progressPercent = 10;
          } else if (status.status === 'in_progress' || status.status === 'crawling' || status.status === 'processing') {
            progressStatus = 'crawling';
            // Use the backend-provided progress if available
            if (status.progress_percentage !== undefined) {
              progressPercent = Math.min(70, status.progress_percentage * 0.7); // Scale to 70% max for crawling phase
            } else {
              const crawlCount = status.pages_scraped || status.pages_processed || 
                (typeof status.visited_urls_total === 'number' ? status.visited_urls_total : (status.visited_urls?.length || 0));
              progressPercent = Math.min(70, (crawlCount || 0) / (status.total_pages || 60) * 70);
            }
            setSetupPhaseLabel('Scraping');
          } else if (status.status === 'embedding') {
            progressStatus = 'embedding';
            progressPercent = 70 + ((status.progress_percentage || 0) / 100 * 20);
            setSetupPhaseLabel('Creating embeddings');
          } else if (status.status === 'extracting_contacts') {
            progressStatus = 'embedding';
            progressPercent = 90 + ((status.progress_percentage || 0) / 100 * 5);
            setSetupPhaseLabel(status.message || 'Processing content');
          } else if (status.status === 'extracting_branding') {
            progressStatus = 'extracting_branding';
            progressPercent = status.progress_percentage || 85;
            setSetupPhaseLabel('Extracting branding');
          } else if (status.status === 'generating_themes' || status.phase === 'generating_themes') {
            progressStatus = 'generating_themes';
            progressPercent = status.progress_percentage || 99;
            setSetupPhaseLabel('Generating themes');
          } else if (status.status === 'crawling_completed') {
            progressStatus = 'embedding';
            progressPercent = 95;
            setSetupPhaseLabel('Finalizing');
          } else if (status.status === 'branding_extracted' || status.status === 'branding_fallback') {
            progressStatus = 'completed';
            progressPercent = status.progress_percentage || 100;
            setSetupPhaseLabel('Branding Ready');

            const brandingPayload = status.branding || status.branding_data;
            if (brandingPayload) {
              setExtractedBranding(brandingPayload);
            }
          } else if (status.status === 'active') {
            progressStatus = 'embedding';
            progressPercent = 95;
            setSetupPhaseLabel('Finalizing');
          } else if (status.status === 'completed') {
            progressStatus = 'completed';
            progressPercent = 100;
            setSetupPhaseLabel('Completed');
          } else if (status.status === 'failed' || status.status === 'error') {
            progressStatus = 'crawling';
            progressPercent = 0;
            setSetupPhaseLabel('Failed');
          }
          
          // Update setup progress bar
          setSetupProgress(progressPercent);
          
          // Use status.message directly from SSE - it already has the correct text like "Scraping page 10/10…"
          // Only update content if status.message exists - don't overwrite with fallback
          const hasStatusMessage = status.message && status.message.trim().length > 0;
          
          // Update the ONE assistant message with crawl-analyzing tag
          setMessages(prev => {
            // Remove ALL duplicates first - keep only the first one found
            let foundIdx = -1;
            const withoutDuplicates = prev.filter((m, i) => {
              if (m.role === 'assistant' && m.messageTag === 'crawl-analyzing') {
                if (foundIdx === -1) {
                  foundIdx = i; // Keep the first one found
                  return true;
                }
                return false; // Remove all other duplicates
              }
              return true; // Keep all other messages
            });
            
            // Update the ONE message if found (it should always exist from initial creation)
            // After filtering, find the message again (indices may have changed)
            const actualIdx = withoutDuplicates.findIndex(m => m.role === 'assistant' && m.messageTag === 'crawl-analyzing');
            if (actualIdx >= 0) {
              const existingMessage = withoutDuplicates[actualIdx];
              const updatedMessage = {
                ...existingMessage,
                timestamp: new Date(),
                isLoading: progressStatus !== 'completed',
                isStreaming: false
              };
              // Only update content if we have a real status message
              // Preserve existing content if no new status message (e.g., from typing effect)
              if (hasStatusMessage) {
                updatedMessage.content = status.message;
              }
              // If no status message, preserve existing content (don't clear it)
              withoutDuplicates[actualIdx] = updatedMessage;
            } else {
              // Message not found - this shouldn't happen, but if it does, don't create duplicate
              // The message should have been created before SSE stream starts
              console.warn('Crawl-analyzing message not found in update logic - this may indicate a timing issue');
            }
            
            // Remove any system messages with crawlProgress (we're using assistant messages now)
            return withoutDuplicates.filter(m => !(m.role === 'system' && m.crawlProgress));
          });
          
          // Handle completion
          if (status.status === 'completed' && status.done) {
            closeStream();
            window.clearTimeout(timeoutId);
            // Update the assistant loading message to completed state
            const crawled = status.pages_scraped || status.pages_processed || 0;
            const total = status.total_pages || 10; // Demo mode always crawls max 10 pages
            setMessages(prev => {
              const updated = [...prev];
              const idx = updated.findIndex(m => m.role === 'assistant' && m.messageTag === 'crawl-analyzing');
              if (idx >= 0) {
                updated[idx] = {
                  ...updated[idx],
                  isLoading: false,
                  isStreaming: false
                };
              }
              return updated;
            });

            // Ensure variables are valid before creating message
            const crawledCount = typeof crawled === 'number' ? crawled : 0;
            const totalCount = typeof total === 'number' ? total : crawledCount;

            // Log completion but don't show redundant message
            console.log('Scrape completed:', { crawled: crawledCount, total: totalCount });

            // Ensure an initial conversation tab exists immediately upon onboarding completion
            if (!hasCreatedInitialConversation && realClientId) {
              const newConvId = String(conversations.length);
              setActiveConversationId(newConvId);
              setHasCreatedInitialConversation(true);

              // Capture current state for the conversation
              const conversationState = getCurrentConversationState();

              setConversations(prev => {
                const newConversation = {
                  id: newConvId,
                  clientId: conversationState.realClientId,
                  title: conversationState.clientDisplayName || conversationState.clientDetails.company_name || 'Training Complete',
                  messages: conversationState.messages,
                  created_at: new Date().toISOString(),
                  websiteUrl: conversationState.websiteUrl,
                  clientName: conversationState.clientDisplayName || conversationState.clientDetails.company_name || '',
                  state: conversationState
                };
                const updatedConversations = [...prev, newConversation];
                try { localStorage.setItem('demo_conversations', JSON.stringify(updatedConversations)); } catch {}
                return updatedConversations;
              });
            }
            // Wait a moment to ensure all processing is truly complete
            setTimeout(() => {
              // Kick off streaming Q&A (showQAGenerationProgress is called inside handleCrawlingComplete)
              handleCrawlingComplete(clientId);
            }, 1000);
          } else if (status.status === 'completed') {
            // Preliminary completion event (likely before branding ready); keep stream open
            console.log('Received preliminary completion update, waiting for finalization...');
          } else if (status.status === 'awaiting_branding') {
            progressStatus = 'extracting_branding';
            progressPercent = status.progress_percentage || 90;
            setSetupPhaseLabel('Extracting branding');
          } else if (status.status === 'failed' || status.status === 'error') {
            closeStream();
            window.clearTimeout(timeoutId);
            toast.error('Failed to process website');
            setIsProcessing(false);
          }
          
          // Note: Assistant loading message is now updated above in the main message update logic
          // This section is no longer needed as we update the assistant message directly with progress
          
        } catch (error) {
          console.error('Error parsing status update:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        if (closedByClient || eventSource.readyState === EventSource.CLOSED) {
          window.clearTimeout(timeoutId);
          console.log('Status stream closed gracefully');
          return;
        }
        console.error('EventSource error:', error);
        closeStream();
        window.clearTimeout(timeoutId);
        toast.error('Connection lost. Please refresh and try again.');
        setIsProcessing(false);
      };
      
    } catch (error) {
      console.error('Error setting up status stream:', error);
      toast.error('Failed to connect to status stream');
      setIsProcessing(false);
    }
  };

  const generateRealQA = async (clientId: string) => {
    try {
      // Use streaming Q&A generation
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/stream-generate-qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          client_id: clientId,
          num_questions: 3,
          selected_sources: Array.from(selectedPages).slice(0, 3) // Use first 3 pages for demo
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate Q&A');
      }
      
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      const collectedQA: any[] = [];
      let qaSessionId: string = '';
      let buffer = '';
      let shouldStop = false;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE events in the buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            if (dataStr) {
              try {
                const data = JSON.parse(dataStr);
                
                if (data.type === 'skipped') {
                  // Q&A generation skipped (e-commerce bot or insufficient content)
                  console.log('Q&A generation skipped:', data.message);
                  handleCrawlingComplete(clientId);
                  shouldStop = true;
                  break;
                } else if (data.type === 'qa_pair') {
                  collectedQA.push(data.qa_pair);
                } else if (data.type === 'complete') {
                  qaSessionId = data.session_id;
                  // Add to Q&A sessions
                  setQaSessions(prev => [...prev, {
                    session_id: qaSessionId,
                    qa_pairs: collectedQA,
                    created_at: new Date().toISOString(),
                    sources: data.sources_used
                  }]);
                } else if (data.type === 'error') {
                  console.error('Q&A generation error:', data.message);
                  handleCrawlingComplete(clientId);
                  shouldStop = true;
                  break;
                }
              } catch (error) {
                console.error('Error parsing SSE data:', error);
              }
            }
          }
        }

        if (shouldStop) {
          break;
        }
      }
      
      // Note: This is a legacy function - progress bubble should be managed by the main streaming flow
      // Do not remove progress bubble here as it interferes with the main Q&A generation
      
      // handleCrawlingComplete(clientId); // Commented out to avoid double calling
      
    } catch (error) {
      console.error('Error generating Q&A:', error);
      // Fall back to demo Q&A
      handleCrawlingComplete(clientId);
    }
  };

  const handleStartCrawling = () => {
    setCurrentStep('crawling');

    const companyName = clientDetails.company_name || 'AI assistant';
    addAssistantMessage(`Excellent! Now I'm going to build your ${companyName}. This is where the magic happens... ✨`, true);

    // Add longer delay to ensure the assistant message is fully displayed before starting progress
    setTimeout(() => {
      // Show crawling progress with page titles
      let progress = 0;
      const selectedPagesList = Array.from(selectedPages);
      const selectedPagesDetails = preScannedPages.filter(p => selectedPages.has(p.url));
      let currentPageIndex = 0;

      const progressInterval = setInterval(() => {
      progress += 5;
      currentPageIndex = Math.floor((progress / 100) * selectedPagesList.length);
      const currentPage = selectedPagesDetails[Math.min(currentPageIndex, selectedPagesDetails.length - 1)];
      
      const crawlProgress: CrawlProgress = {
        status: progress < 60 ? 'crawling' : 'embedding',
        progress,
        message: progress < 60 
          ? `Extracting content from: ${currentPage?.title || 'page'}...` 
          : 'Training AI with your website content...',
        pagesProcessed: Math.floor((progress / 100) * selectedPages.size),
        totalPages: selectedPages.size
      };

      // Update or add progress message
      setMessages(prev => {
        const newMessages = [...prev];
        const lastProgressIndex = newMessages.findIndex(m => m.crawlProgress);
        
        const progressMessage: Message = {
          role: 'system',
          content: '',
          timestamp: new Date(),
          crawlProgress
        };

        if (lastProgressIndex >= 0) {
          newMessages[lastProgressIndex] = progressMessage;
        } else {
          newMessages.push(progressMessage);
        }
        
        return newMessages;
      });

      if (progress >= 100) {
        clearInterval(progressInterval);
        setTimeout(() => {
          handleCrawlingComplete(realClientId || 'demo-client');
        }, 500);
      }
    }, 300);
    }, 1500); // Delay before showing progress
  };

  const showQAGenerationProgress = (clientId: string) => {
    // Remove any existing progress bubble
    setMessages(prev => prev.filter(m => !m.crawlProgress));
    
    // Show Q&A generation progress with a visual indicator
    const qaProgressMessage: Message = {
      role: 'system',
      content: '',
      timestamp: new Date(),
      crawlProgress: {
        status: 'embedding',  // Reuse embedding status for Q&A generation
        progress: 10,
        message: 'Generating intelligent Q&As from your content...',
        pagesProcessed: 0,
        totalPages: 5
      }
    };
    setMessages(prev => [...prev, qaProgressMessage]);
    
    // Simulate progress updates
    let progress = 10;
    const progressInterval = setInterval(() => {
      progress = Math.min(progress + 15, 90);
      setMessages(prev => {
        const updated = [...prev];
        const progressMsg = updated[updated.length - 1];
        if (progressMsg && progressMsg.crawlProgress) {
          progressMsg.crawlProgress.progress = progress;
          progressMsg.crawlProgress.message = `Analyzing content and generating Q&As...`;
        }
        return [...updated];
      });
      
      if (progress >= 90) {
        clearInterval(progressInterval);

        // Final message to indicate completion
        setTimeout(() => {
          // addAssistantMessage("Content analysis complete! Preparing Q&A...", true);
        }, 500);
      }
    }, 500);
    
    // Store interval for cleanup
    setTimeout(() => {
      if (progressInterval) {
        clearInterval(progressInterval);
        console.log('Progress interval cleared after timeout');
      }
    }, 10000);
  };

const createPreviewOption = (clientIdParam: string): OptionButton => ({
  id: 'preview-widget',
  label: '👁️ Preview Widget on Your Website',
  description: 'See how the AI assistant will look on your site',
  action: () => {
    console.log('Preview button clicked');
    if (!realClientId && clientIdParam) {
      setRealClientId(clientIdParam);
    }

    // Close selector and retrigger preview with a slight delay to ensure theme updates land
    setShowThemeSelector(false);
    setShowWebsitePreview(false);
    setTimeout(() => {
      console.log('Showing preview after branding delay');
      setShowWebsitePreview(true);
    }, 800);
  }
});

const promptForQAGeneration = (clientId: string) => {
  return new Promise<boolean>((resolve) => {
    setIsProcessing(false);
    setWaitingForInput(false);
    setCollectingField(null);

    const clearOptions = () => {
      setMessages(prev => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
        if (lastMessage?.options) {
          delete lastMessage.options;
        }
        return updated;
      });
    };

    const qaPromptOptions: OptionButton[] = [
      {
        id: 'generate-qa-now',
        label: '✅ Generate Q&A Cards',
        description: 'Create sample questions and answers from your content',
        action: () => {
          clearOptions();
          addUserMessage('Generate Q&A cards');
          setWaitingForInput(false);
          setIsProcessing(true);
          resolve(true);
        }
      },
      {
        id: 'skip-qa-generation',
        label: '⏭️ Skip For Now',
        description: 'You can always generate them later',
        action: () => {
          clearOptions();
          addUserMessage('Skip Q&A generation');
          const trainingSummary = `🤖 Your ${clientDetails.company_name || 'AI'} is fully trained on your selected pages. Try asking anything about your business — I'm ready!`;
          addAssistantMessage(
            trainingSummary,
            true,
            {
              options: [createPreviewOption(clientId)]
            }
          );
          setQaCards([]);
          setIsProcessing(false);
          setWaitingForInput(true);

          // Ensure a conversation tab exists even when skipping Q&A
          if (!hasCreatedInitialConversation && clientId) {
            setTimeout(() => {
              const newConvId = String(conversations.length);
              setActiveConversationId(newConvId);
              setHasCreatedInitialConversation(true);

              // Build messages including the latest assistant summary
              const currentMessages = messages.filter(m => m.role !== 'system' || m.crawlProgress);
              const allMessages = [
                ...currentMessages,
                {
                  role: 'assistant' as const,
                  content: trainingSummary,
                  timestamp: new Date()
                }
              ];

              const conversationState = {
                messages: allMessages,
                conversationHistory,
                conversationId,
                clientDisplayName,
                realClientId: clientId,
                websiteUrl,
                clientDetails,
                botPurpose,
                botPersonality,
                selectedPages: Array.from(selectedPages),
                preScannedPages,
                qaCards: [],
                qaSessions,
                sessionId,
                activeSessionId,
                currentStep: 'demo' as const,
                collectingField,
                waitingForInput: true,
                setupProgress,
                setupPhaseLabel
              };

              setConversations(prev => {
                const newConversation = {
                  id: newConvId,
                  clientId: clientId,
                  title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                  messages: allMessages,
                  created_at: new Date().toISOString(),
                  websiteUrl: websiteUrl,
                  clientName: clientDisplayName || clientDetails.company_name || '',
                  state: conversationState
                };
                const updated = [...prev, newConversation];
                try { localStorage.setItem('demo_conversations', JSON.stringify(updated)); } catch {}
                return updated;
              });
            }, 100);
          }
          resolve(false);
        }
      }
    ];

    addAssistantMessage(
  "Would you like me to generate some sample Q&A pairs using the content I scraped?",
      true,
      { options: qaPromptOptions }
    );

    // Ensure interactions remain live by leaving the message in place
    // until one of the option handlers resolves the promise.
  });
};

  const updateQAProgress = (progressData: any) => {
    // Update Q&A generation progress
    const progressMessage = progressData.message || 'Generating intelligent Q&A from your content...';
    
    // Update existing message with loading state
    setMessages(prev => {
      const updated = [...prev];
      const idx = updated.findIndex(m => m.messageTag === 'qa-progress');
      if (idx >= 0) {
        updated[idx] = {
          ...updated[idx],
          content: progressMessage,
          isLoading: true,
          isThinking: true,
          timestamp: new Date()
        };
      } else {
        // If no existing message, add one with loading state
        updated.push({
          role: 'assistant',
          content: progressMessage,
          timestamp: new Date(),
          isLoading: true,
          isThinking: true,
          messageTag: 'qa-progress'
        });
      }
      return updated;
    });
  };

  const handleCrawlingComplete = async (clientId: string) => {
    if (
      currentStepRef.current === 'demo' ||
      extractedBrandingRef.current?.colors?.primary ||
      handleCrawlingCompleteRunningRef.current ||
      brandingMessageShownRef.current
    ) {
      console.log('handleCrawlingComplete - skipping duplicate run', {
        currentStep: currentStepRef.current,
        hasBranding: !!extractedBrandingRef.current?.colors?.primary,
        running: handleCrawlingCompleteRunningRef.current,
        brandingMessageShown: brandingMessageShownRef.current
      });
      return;
    }

    handleCrawlingCompleteRunningRef.current = true;

    try {
      // Keep isProcessing true during Q&A generation
      setCurrentStep('demo');

      // Check immediately if branding is already ready (from stream or sync batch scrape)
      let brandingReady = !!extractedBrandingRef.current?.colors?.primary;

      if (brandingReady) {
        console.log('Branding already available from stream:', extractedBrandingRef.current);
      } else {
        try {
          const immediateCheck = await fetch(`${getApiRootUrl()}/api/demo-enhanced/client-branding/${clientId}`);
          if (immediateCheck.ok) {
            const data = await immediateCheck.json();
            if (data?.branding?.colors?.primary && !data?.is_default) {
              brandingReady = true;
              extractedBrandingRef.current = data.branding;
              setExtractedBranding(data.branding);
              console.log('Branding already completed (sync processing):', data.branding);
            }
          }
        } catch (error) {
          console.error('Error checking immediate branding status:', error);
        }
      }

      // Only show processing message and poll if branding not ready yet
      if (!brandingReady) {
        // Show status updates for post-crawl processing
        addAssistantMessage("🔄 Generating AI embeddings from your content...", true);

        // Poll for branding completion (which indicates all processing is done)
        let pollCount = 0;
        const maxPolls = 20; // Max 20 seconds of polling

        while (!brandingReady && pollCount < maxPolls) {
          await new Promise(resolve => setTimeout(resolve, 1000));

          try {
            const checkResponse = await fetch(`${getApiRootUrl()}/api/demo-enhanced/client-branding/${clientId}`);
            if (checkResponse.ok) {
              const data = await checkResponse.json();
              // Check if actual branding is extracted (not just defaults)
              if (data?.branding?.colors?.primary && !data?.is_default) {
                brandingReady = true;
                console.log('Branding extraction completed:', data.branding);
                extractedBrandingRef.current = data.branding;
                setExtractedBranding(data.branding);
              } else if (pollCount === 5) {
                // After 5 seconds, show another status
                addAssistantMessage("🎨 Analyzing your brand colors and styles...", true);
              } else if (pollCount === 10) {
                // After 10 seconds, show progress
                addAssistantMessage("⏳ Almost done setting up your AI assistant...", true);
              }
            }
          } catch (error) {
            console.error('Error checking branding status:', error);
          }

          pollCount++;
        }
      }

      // If branding still not ready after polling, proceed with defaults
      if (!brandingReady) {
        console.log('Branding extraction taking too long, proceeding with defaults');
        addAssistantMessage("✨ Setting up with default theme (you can customize it later)...", true);
      }
      // Skip brand extraction message - not needed

      // Now fetch the final branding data
      try {
        const brandingResponse = await fetch(`${getApiRootUrl()}/api/demo-enhanced/client-branding/${clientId}`);
        if (brandingResponse.ok) {
          const brandingData = await brandingResponse.json();
          const brandingPayload = brandingData?.branding;

          if (brandingPayload) {
            setExtractedBranding(brandingPayload);
            extractedBrandingRef.current = brandingPayload;
            console.log('Extracted branding:', brandingPayload);

            // Check if this is default branding
            const isDefaultBranding = brandingPayload?.extraction_metadata?.used_defaults;

            if (isDefaultBranding) {
              console.log('Using default branding colors and fonts');
              addAssistantMessage(
                `✨ I've set up a beautiful default theme for your AI chatbot. You can customize the colors and fonts in the theme selector!`,
                true
              );
              setBrandingMessageShown(true); // Mark as shown for default branding too
            } else {
              // We have actual branding extracted from the site - check if message already shown
              const primaryColor = brandingPayload.colors?.primary;
              const secondaryColor = brandingPayload.colors?.secondary;
              const primaryFont = brandingPayload.fonts?.primary;

              // Check if we've already shown this branding message
              if (!brandingMessageShown) {
                const colorInfo = primaryColor ? ` (primary: ${primaryColor})` : '';
                const fontInfo = primaryFont && primaryFont !== 'Inter' ? ` and fonts: ${primaryFont}` : '';

                // addAssistantMessage(
                //   `✨ I've extracted your brand colors${colorInfo}${fontInfo}. These will be applied to your AI chatbot!`,
                //   true
                // );

                setBrandingMessageShown(true); // Mark as shown

                console.log('Extracted actual branding from site:', {
                  primaryColor,
                  secondaryColor,
                  primaryFont,
                  totalColors: brandingPayload.colors?.all_colors?.length || 0,
                  brandColorsFound: brandingPayload.extraction_metadata?.brand_colors_found || 0
                });
              } else {
                console.log('Branding message already shown (state), skipping duplicate message');
              }
            }
          } else {
            console.warn('Branding response had no payload, using fallback defaults');
            setExtractedBranding({ fallback: true });
            setBrandingMessageShown(true); // Mark as shown for fallback case too
          }

          if (realClientId) {
            fetchThemeVariants(realClientId);
          }

          // Reduced wait time after fetching variants
          await new Promise(resolve => setTimeout(resolve, 200));
        } else {
          console.warn('Branding request failed, status:', brandingResponse.status);
          setExtractedBranding((prev: any) => prev || { fallback: true });
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
        setExtractedBranding((prev: any) => prev || { fallback: true });
      } finally {
        handleCrawlingCompleteRunningRef.current = false;
      }
    } catch (error) {
      console.error('Error in handleCrawlingComplete:', error);
      handleCrawlingCompleteRunningRef.current = false;
    }

    // Skip Q&A generation for e-commerce bots
    if (botType === 'ecommerce') {
      console.log('Skipping Q&A generation for e-commerce bot');
      addAssistantMessage("🛒 Your e-commerce assistant is ready! It can help customers find products, check prices, and make shopping decisions in real-time.", true);
      // Mark setup as complete
      setIsProcessing(false);
      setWaitingForInput(true);
      // We're already in demo step from line 3106 above, so just return
      return;
    }

    // Always ask before generating Q&A (no automatic run) for non-ecommerce bots
    let userWantsQA = await promptForQAGeneration(clientId);

    if (!userWantsQA) {
      return;
    }

    // Show Q&A generation progress and wait a moment for user to see it
    showQAGenerationProgress(clientId);
    
    // Wait a moment before starting the actual Q&A generation stream
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Generate real Q&A using the streaming service
    try {
      // Get selected pages (use first 5 pages for Q&A generation)
      const selectedPagesList = Array.from(selectedPages).slice(0, 5);
      
      if (!clientId) {
        throw new Error('No client ID available');
      }
      
      // Use fetch with streaming for Q&A generation
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/stream-generate-qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId || 'demo-session',
          client_id: clientId,
          num_questions: 5,
          selected_sources: selectedPagesList
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start Q&A generation stream');
      }

    const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      const collectedQA: QACard[] = [];
      let buffer = '';
    let lastEventTs = Date.now();

    // Stall timeout: fallback to non-streaming after 30s of no events
    const stallMs = 30000;
    const stallTimer = setInterval(() => {
      if (Date.now() - lastEventTs > stallMs) {
        clearInterval(stallTimer);
        console.warn('Q&A stream stalled, falling back');
        handleCrawlingCompleteFallback(clientId);
      }
    }, 5000);

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          clearInterval(stallTimer);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        lastEventTs = Date.now();
        
        // Process complete SSE events in the buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            if (dataStr) {
              try {
                const data = JSON.parse(dataStr);
                
                if (data.type === 'progress') {
                  // Update Q&A generation progress
                  updateQAProgress(data);
                } else if (data.type === 'qa_pair') {
                  // Collect Q&A pairs as they come in
                  const qa = data.qa_pair;
                  collectedQA.push({
                    question: qa.question,
                    answer: qa.answer,
                    source: qa.sources?.[0] || websiteUrl,
                    id: generateUUID()
                  });
                  
                  // Update progress with Q&A counter
                  const questionNumber = data.index + 1;
                  const totalQuestions = data.total;
                  updateQAProgress({
                    progress: data.progress || (70 + ((questionNumber / totalQuestions) * 25)),
                    message: `Generated ${questionNumber}/${totalQuestions} questions with answers...`,
                    processed: questionNumber,
                    total: totalQuestions
                  });
                } else if (data.type === 'complete') {
                  clearInterval(stallTimer);
                  const totalPairs = Array.isArray(collectedQA) ? collectedQA.length : 0;
                  // Store Q&A session info (ensure not zero if data provided in complete)
                  setQaSessions(prev => [...prev, {
                    session_id: data.session_id,
                    qa_pairs: totalPairs > 0 ? collectedQA : (Array.isArray(data.qa_pairs) ? data.qa_pairs.map((q:any)=>({question:q.question, answer:q.answer, source:(q.sources&&q.sources[0])||websiteUrl, id: generateUUID()})) : []),
                    sources: selectedPagesList,
                    created_at: new Date().toISOString()
                  }]);

                  const finalQAs = totalPairs > 0 ? collectedQA : (Array.isArray(data.qa_pairs) ? data.qa_pairs.map((q:any)=>({question:q.question, answer:q.answer, source:(q.sources&&q.sources[0])||websiteUrl, id: generateUUID()})) : []);
                  
                  // Remove progress bubble and Q&A loading message when generation completes
                  setMessages(prev => prev.filter(m => !m.crawlProgress && m.messageTag !== 'qa-progress'));
                  setIsProcessing(false);
                  
                  setTimeout(() => {
                    addAssistantMessage(
                      `🎉 Fantastic! Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some real questions it can answer based on your actual website content:`,
                      true,
                      { qaCards: finalQAs }
                    );
                    setTimeout(() => {
                      // Find the scanComplete data from previous messages
                      const scanCompleteMessage = messages.find(m => m.scanComplete);
                      const scanData = scanCompleteMessage?.scanComplete;
                      
                      // Get details about selected pages
                      const selectedPagesList = Array.from(selectedPages);
                      const selectedPagesDetails = preScannedPages.filter(p => selectedPages.has(p.url));
                      const selectedCount = selectedPagesList.length;
                      
                      let trainingMessage = `🤖 Your ${clientDetails.company_name || 'AI'} is now fully trained!`;
                      
                      if (scanData && selectedCount > 0) {
                        // Get top categories from scan data
                        const topCategories = scanData.categories
                          .slice(0, 3)
                          .map(c => c.category);
                        
                        const categoryText = topCategories.length > 0 
                          ? topCategories.join(', ') 
                          : 'your content';
                        
                        // Create specific examples based on categories
                        const categoryExamples: Record<string, string> = {
                          'products': 'product details, pricing, and features',
                          'services': 'service offerings and capabilities',
                          'about': 'company history, team, and mission',
                          'contact': 'contact information and locations',
                          'support': 'FAQs and customer support',
                          'documentation': 'guides and technical documentation',
                          'blog': 'articles and insights',
                          'portfolio': 'past work and case studies'
                        };
                        
                        const examples = topCategories
                          .map(cat => categoryExamples[cat.toLowerCase()] || cat.toLowerCase())
                          .slice(0, 2);
                        
                        const examplesText = examples.length > 0 
                          ? `, including ${examples.join(' and ')}` 
                          : '';
                        
                        trainingMessage = `🤖 Your ${clientDetails.company_name || 'AI'} is now fully trained on ${selectedCount} carefully selected pages${examplesText}. It can answer detailed questions about your ${topCategories.length > 0 ? topCategories[0].toLowerCase() : 'business'} and provide accurate information from your website content.`;
                      } else if (selectedCount > 0) {
                        // Fallback when no scan data but we know selected pages
                        trainingMessage = `🤖 Your ${clientDetails.company_name || 'AI'} is now fully trained on ${selectedCount} pages from your website. Ask anything about your business — I'm using your actual site content.`;
                      } else {
                        // Final fallback
                        trainingMessage = `🤖 Your ${clientDetails.company_name || 'AI'} is now fully trained! Ask anything about your business — I'm using your site content.`;
                      }
                      
                      addAssistantMessage(
                        trainingMessage,
                        true,
                        {
                          options: [
                            {
                              id: 'preview-widget',
                              label: '👁️ Preview Widget on Your Website',
                              description: 'See how the AI assistant will look on your site',
                              action: () => {
                                console.log('Preview button clicked');
                                //addUserMessage('Show me how it will look on my website');
                                console.log('Calling loadPagePreview, clientId from scope:', clientId);
                                // Set the realClientId if not already set
                                if (!realClientId && clientId) {
                                  setRealClientId(clientId);
                                }
                                // Add a delay to ensure branding is fully applied
                                setTimeout(() => {
                                  console.log('Showing preview after branding delay');
                                  // Toggle preview visibility
                                  setShowWebsitePreview(prev => !prev);
                                }, 2000); // 2 second delay to ensure theme is updated
                              }
                            }
                          ]
                        }
                      );
                      setWaitingForInput(true);
                      setQaCards(finalQAs);
                      
                      // Create initial conversation after training completes
                      // Use clientId from the function parameter which is guaranteed to exist
                      if (!hasCreatedInitialConversation && clientId) {
                        setTimeout(() => {
                        const newConvId = String(conversations.length);
                          setActiveConversationId(newConvId);
                          setHasCreatedInitialConversation(true);
                          
                          // Get the current state with all training messages
                          const currentMessages = messages.filter(m => m.role !== 'system' || m.crawlProgress);
                          
                          // Add the training complete messages to the current messages
                          const allMessages = [
                            ...currentMessages,
                            {
                              role: 'assistant' as const,
                              content: `🎉 Fantastic! Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some real questions it can answer based on your actual website content:`,
                              timestamp: new Date(),
                              qaCards: finalQAs
                            },
                            {
                              role: 'assistant' as const,
                              content: trainingMessage,
                              timestamp: new Date()
                            }
                          ];
                          
                          // Create the conversation with complete state
                          const conversationState = {
                            messages: allMessages,
                            conversationHistory,
                            conversationId,
                            clientDisplayName,
                            realClientId: clientId,
                            websiteUrl,
                            clientDetails,
                            botPurpose,
                            botPersonality,
                            selectedPages: Array.from(selectedPages),
                            preScannedPages,
                            qaCards: finalQAs,
                            qaSessions,
                            sessionId,
                            activeSessionId,
                            currentStep: 'demo' as const,
                            collectingField,
                            waitingForInput: true,
                            setupProgress,
                            setupPhaseLabel
                          };
                          
                          setConversations(prev => {
                            const newConversation = {
                              id: newConvId,
                              clientId: clientId,
                              title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                              messages: allMessages,
                              created_at: new Date().toISOString(),
                              websiteUrl: websiteUrl,
                              clientName: clientDisplayName || clientDetails.company_name || '',
                              state: conversationState
                            };
                            
                            const updatedConversations = [...prev, newConversation];
                            
                            // Save to localStorage with the updated conversations
                            localStorage.setItem('demo_conversations', JSON.stringify(updatedConversations));
                            
                            return updatedConversations;
                          });
                          
                        }, 100); // Small delay to ensure all state is updated
                      }
                    }, 2000);
                  }, 1000);
                } else if (data.type === 'error') {
                  throw new Error(data.message);
                }
              } catch (error) {
                console.error('Error parsing SSE data:', error);
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error generating Q&A:', error);
      handleCrawlingCompleteFallback(clientId);
    }
  };

  const handleCrawlingCompleteFallback = async (clientId: string) => {
    // Try the non-streaming endpoint as fallback
    try {
      const selectedPagesList = Array.from(selectedPages).slice(0, 5);
      const response = await fetch(`${getApiRootUrl()}/api/demo-enhanced/generate-qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId || 'demo-session',
          client_id: clientId,
          num_questions: 5,
          selected_sources: selectedPagesList
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate Q&A');
      }

      const qaData = await response.json();
      const realQA: QACard[] = qaData.qa_pairs.map((qa: any) => ({
        question: qa.question,
        answer: qa.answer,
        source: qa.sources?.[0] || websiteUrl,
        id: generateUUID()
      }));

      // Store Q&A session info
      setQaSessions(prev => [...prev, {
        session_id: qaData.session_id,
        qa_pairs: realQA,
        sources: selectedPagesList,
        created_at: new Date().toISOString()
      }]);

      setTimeout(() => {
        addAssistantMessage(
          `🎉 Fantastic! Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some real questions it can answer based on your actual website content:`,
          true,
          { qaCards: realQA }
        );
        
        setTimeout(() => {
          // Find the scanComplete data from previous messages
          const scanCompleteMessage = messages.find(m => m.scanComplete);
          const scanData = scanCompleteMessage?.scanComplete;
          
          // Get details about selected pages
          const selectedPagesListFull = Array.from(selectedPages);
          const selectedPagesDetails = preScannedPages.filter(p => selectedPages.has(p.url));
          const selectedCount = selectedPagesListFull.length;
          
          let trainingMessage = "Try asking me any question about your business! I'll answer using the knowledge from your website.";
          
          if (scanData && selectedCount > 0) {
            // Get top categories from scan data (we can't filter by selected pages since PageInfo doesn't have category)
            // So we'll just use the top categories from the scan
            const topCategories = scanData.categories
              .slice(0, 3)
              .map(c => c.category);
            
            const categoryExamples: Record<string, string> = {
              'products': 'product features and pricing',
              'services': 'service offerings',
              'about': 'company information',
              'contact': 'how to reach you',
              'support': 'customer support',
              'documentation': 'guides and docs',
              'blog': 'insights and articles',
              'portfolio': 'past work'
            };
            
            const examples = topCategories
              .map(cat => categoryExamples[cat.toLowerCase()] || cat.toLowerCase())
              .slice(0, 2);
            
            const examplesText = examples.length > 0 
              ? ` I can answer questions about ${examples.join(' and ')}, and more!` 
              : '';
            
            trainingMessage = `I'm ready to help! I've learned from ${selectedCount} pages of your website content.${examplesText} Try asking me anything about your business.`;
          } else if (selectedCount > 0) {
            trainingMessage = `I'm ready to help! I've learned from ${selectedCount} pages of your website. Try asking me anything about your business!`;
          }
          
          addAssistantMessage(
            trainingMessage,
            true
          );
          setWaitingForInput(true);
          setQaCards(realQA);
          
          // Create initial conversation after training completes (fallback path)
          if (!hasCreatedInitialConversation && realClientId) {
            setTimeout(() => {
              const newConvId = String(conversations.length);
              setActiveConversationId(newConvId);
              setHasCreatedInitialConversation(true);
              
              // Get the current state with all training messages
              const currentMessages = messages.filter(m => m.role !== 'system' || m.crawlProgress);
              
              // Add the training complete messages to the current messages
              const allMessages = [
                ...currentMessages,
                {
                  role: 'assistant' as const,
                  content: `🎉 Fantastic! Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some real questions it can answer based on your actual website content:`,
                  timestamp: new Date(),
                  qaCards: realQA
                },
                {
                  role: 'assistant' as const,
                  content: trainingMessage,
                  timestamp: new Date()
                }
              ];
              
              // Create the conversation with complete state
              const conversationState = {
                messages: allMessages,
                conversationHistory,
                conversationId,
                clientDisplayName,
                realClientId,
                websiteUrl,
                clientDetails,
                botPurpose,
                botPersonality,
                selectedPages: Array.from(selectedPages),
                preScannedPages,
                qaCards: realQA,
                qaSessions,
                sessionId,
                activeSessionId,
                currentStep: 'demo' as const,
                collectingField,
                waitingForInput: true,
                setupProgress,
                setupPhaseLabel
              };

              setConversations(prev => [...prev, {
                id: newConvId,
                clientId: realClientId,
                title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                messages: allMessages,
                created_at: new Date().toISOString(),
                websiteUrl: websiteUrl,
                clientName: clientDisplayName || clientDetails.company_name || '',
                state: conversationState
              }]);

              // Also save to localStorage immediately
              const allConvs = [...conversations, {
                id: newConvId,
                clientId: realClientId,
                title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                messages: allMessages,
                created_at: new Date().toISOString(),
                websiteUrl: websiteUrl,
                clientName: clientDisplayName || clientDetails.company_name || '',
                state: conversationState
              }];
              localStorage.setItem('demo_conversations', JSON.stringify(allConvs));

              // Per requirement: do not persist to backend. LocalStorage is sufficient
              
            }, 100); // Small delay to ensure all state is updated
          }
        }, 2000);
      }, 1000);
      
    } catch (error) {
      console.error('Error in fallback Q&A generation:', error);
      
      // Fallback to dummy Q&A if generation fails
      const demoQA: QACard[] = [
        {
          question: "What services do you offer?",
          answer: `Based on your website, you offer comprehensive solutions that help businesses succeed. Your main services include professional consulting, implementation, and ongoing support.`,
          source: "/services",
          id: generateUUID()
        },
        {
          question: "How can I contact you?",
          answer: `You can reach out through the contact form on your website, email at info@${websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}, or call during business hours.`,
          source: "/contact",
          id: generateUUID()
        },
        {
          question: "What makes you different?",
          answer: "Your unique value proposition includes personalized service, cutting-edge technology, and a proven track record of successful projects with measurable results.",
          source: "/about",
          id: generateUUID()
        }
      ];

      setTimeout(() => {
        addAssistantMessage(
          `🎉 Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some examples of what it can answer:`,
          true,
          { qaCards: demoQA }
        );
        
        setTimeout(() => {
          // Even in fallback, try to provide a more specific message
          const selectedCount = selectedPages.size;
          let trainingMessage = "Try asking me any question about your business! I'll answer using the knowledge from your website.";
          
          if (selectedCount > 0) {
            trainingMessage = `I'm ready to help! I've prepared answers based on ${selectedCount} pages from your website. Try asking me anything about your business!`;
          }
          
          addAssistantMessage(
            trainingMessage,
            true
          );
          setWaitingForInput(true);
          setQaCards(demoQA);
          
          // Create initial conversation after training completes (dummy fallback path)
          if (!hasCreatedInitialConversation && realClientId) {
            setTimeout(() => {
              const newConvId = String(conversations.length);
              setActiveConversationId(newConvId);
              setHasCreatedInitialConversation(true);
              
              // Get the current state with all training messages
              const currentMessages = messages.filter(m => m.role !== 'system' || m.crawlProgress);
              
              // Add the training complete messages to the current messages
              const allMessages = [
                ...currentMessages,
                {
                  role: 'assistant' as const,
                  content: `🎉 Your ${clientDetails.company_name || 'AI assistant'} is trained and ready. Here are some examples of what it can answer:`,
                  timestamp: new Date(),
                  qaCards: demoQA
                },
                {
                  role: 'assistant' as const,
                  content: trainingMessage,
                  timestamp: new Date()
                }
              ];
              
              // Create the conversation with complete state
              const conversationState = {
                messages: allMessages,
                conversationHistory,
                conversationId,
                clientDisplayName,
                realClientId,
                websiteUrl,
                clientDetails,
                botPurpose,
                botPersonality,
                selectedPages: Array.from(selectedPages),
                preScannedPages,
                qaCards: demoQA,
                qaSessions,
                sessionId,
                activeSessionId,
                currentStep: 'demo' as const,
                collectingField,
                waitingForInput: true,
                setupProgress,
                setupPhaseLabel
              };
              
              setConversations(prev => [...prev, {
                id: newConvId,
                clientId: realClientId,
                title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                messages: allMessages,
                created_at: new Date().toISOString(),
                websiteUrl: websiteUrl,
                clientName: clientDisplayName || clientDetails.company_name || '',
                state: conversationState
              }]);
              
              // Also save to localStorage immediately
              const allConvs = [...conversations, {
                id: newConvId,
                clientId: realClientId,
                title: clientDisplayName || clientDetails.company_name || 'Training Complete',
                messages: allMessages,
                created_at: new Date().toISOString(),
                websiteUrl: websiteUrl,
                clientName: clientDisplayName || clientDetails.company_name || '',
                state: conversationState
              }];
              localStorage.setItem('demo_conversations', JSON.stringify(allConvs));

              // Per requirement: do not persist to backend. LocalStorage is sufficient

            }, 100); // Small delay to ensure all state is updated
          }
        }, 2000);
      }, 1000);
    }
  };

  const handleQAFeedback = async (messageId: string, cardIndex: number, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => {
    try {
      // If thumbs down without reason, show reason options
      if (type === 'thumbs_down' && !reason) {
        setShowFeedbackReasonFor(`qa-${messageId}-${cardIndex}`);
        return;
      }
      
      // Hide reason options if shown
      setShowFeedbackReasonFor(null);
      
      // Update the Q&A card's feedback state
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId && msg.qaCards) {
          const updatedCards = [...msg.qaCards];
          if (updatedCards[cardIndex]) {
            updatedCards[cardIndex] = {
              ...updatedCards[cardIndex],
              feedback: type,
              feedbackReason: reason
            };
          }
          return { ...msg, qaCards: updatedCards };
        }
        return msg;
      }));
      
      // Submit feedback for the Q&A card
      const qaCard = messages.find(m => m.id === messageId)?.qaCards?.[cardIndex];
      if (qaCard?.id) {
        // Only submit to API if we have a real client ID
        if (realClientId) {
          try {
            await feedbackService.submitFeedback({
              message_id: qaCard.id,
              feedback_type: type,
              reason: reason,
              client_id: realClientId,
              conversation_id: conversationId || undefined,
            });
          } catch (error) {
            console.error('Failed to submit Q&A feedback to API:', error);
            // Continue - we'll still save to localStorage
          }
        }
        
        // Save feedback state to localStorage
        const feedbackKey = 'demo_qa_feedback_state';
        const existingFeedback = JSON.parse(localStorage.getItem(feedbackKey) || '{}');
        existingFeedback[qaCard.id] = {
          type,
          reason: reason || null,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(feedbackKey, JSON.stringify(existingFeedback));
      }
      
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting Q&A feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const handleFeedback = async (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => {
    try {
      // If thumbs down without reason, show reason options
      if (type === 'thumbs_down' && !reason) {
        setShowFeedbackReasonFor(messageId);
        return;
      }
      
      // Hide reason options if shown
      setShowFeedbackReasonFor(null);
      
      // For demo mode, just update UI and localStorage without API call
      if (!realClientId) {
        // Update the message's feedback state in the UI
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, feedback: type, feedbackReason: reason }
            : msg
        ));
        
        // Save feedback state to localStorage for persistence
        const feedbackKey = 'demo_chat_feedback_state';
        const existingFeedback = JSON.parse(localStorage.getItem(feedbackKey) || '{}');
        existingFeedback[messageId] = {
          type,
          reason: reason || null,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(feedbackKey, JSON.stringify(existingFeedback));
        
        toast.success('Thank you for your feedback!');
        return;
      }
      
      // Find the conversation_id for this message
      let conversation_id = conversationId;
      
      // Look through messages to find the conversation_id
      for (const message of messages) {
        if (message.id === messageId) {
          conversation_id = message.metadata?.conversation_id || conversationId;
          break;
        }
      }
      
      await feedbackService.submitFeedback({
        message_id: messageId,
        feedback_type: type,
        reason: reason,
        client_id: realClientId || undefined,
        conversation_id: conversation_id || undefined,
      });
      
      // Update the message's feedback state in the UI
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedback: type, feedbackReason: reason }
          : msg
      ));
      
      // Save feedback state to localStorage for persistence
      const feedbackKey = 'demo_chat_feedback_state';
      const existingFeedback = JSON.parse(localStorage.getItem(feedbackKey) || '{}');
      existingFeedback[messageId] = {
        type,
        reason: reason || null,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(feedbackKey, JSON.stringify(existingFeedback));
      
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };


  const handleDemoChat = async (message: string) => {
    if (!realClientId) {
      toast.error('Client ID not available. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);

    try {
      // Add user message to conversation history
      const updatedHistory = [...conversationHistory, { role: 'user', content: message }];
      setConversationHistory(updatedHistory);

      // If no conversation ID, generate one
      let currentConversationId = conversationId;
      if (!conversationId) {
        // Generate a proper UUID for conversation
        currentConversationId = generateUUID();
        setConversationId(currentConversationId);
      }

      // Create streaming message placeholder with thinking state
      const assistantMessageId = generateUUID();
      const assistantMessage: Message = {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isThinking: true,
        id: assistantMessageId,
        metadata: {
          conversation_id: currentConversationId || undefined
        },
        isKnowledgeResponse: true // This is a knowledge-based response
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Call the main chat stream API
      const response = await fetch(`${getApiRootUrl()}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Conversation-Id': conversationId || '',
          'X-Widget-Mode': 'true' // This helps the backend know it's a widget/demo request
        },
        body: JSON.stringify({
          client_id: realClientId,
          message: JSON.stringify(updatedHistory),
          conversation_id: conversationId,
          metadata: {
            source: 'demo',
            session_id: sessionId
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get chat response');
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response stream available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';
      let eventType = '';
      let isThinking = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              // Handle thinking state
              if (eventType === 'thinking') {
                // Already showing thinking state, no need to update
              }
              // Handle message chunks
              else if (eventType === 'message' && data.type === 'text_chunk') {
                if (isThinking) {
                  isThinking = false;
                  // First chunk - switch from thinking to loading
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                      lastMessage.isThinking = false;
                      lastMessage.isLoading = true;
                      lastMessage.content = '';
                    }
                    return newMessages;
                  });
                  // Small delay to show loading state
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
                fullResponse += data.content;

                // Update the last message with streaming content
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage && lastMessage.role === 'assistant') {
                    lastMessage.content = fullResponse;
                    lastMessage.isLoading = false;
                    lastMessage.isThinking = false;
                    lastMessage.isStreaming = true;
                  }
                  return newMessages;
                });
              }
              // Handle completion
              else if (eventType === 'complete') {
                // Finalize the message
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage && lastMessage.role === 'assistant') {
                    lastMessage.content = data.content || fullResponse;
                    lastMessage.isTyping = false;
                    lastMessage.isThinking = false;
                    lastMessage.isLoading = false;
                    lastMessage.isStreaming = false;
                  }
                  return newMessages;
                });

                // Update conversation history
                setConversationHistory([...updatedHistory, { role: 'assistant', content: data.content || fullResponse }]);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          } else if (line === '') {
            // Empty line signals end of event
            eventType = '';
          }
        }
      }

      setIsProcessing(false);
      setWaitingForInput(true);
    } catch (error) {
      console.error('Error in chat stream:', error);

      // Remove the typing message
      setMessages(prev => prev.slice(0, -1));

      // Add error message
      addAssistantMessage(
        "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        true
      );

      setIsProcessing(false);
      setWaitingForInput(true);
    }
  };

  // Removed signup prompt - unlimited demo access

  // Removed all signup-related functions for unlimited demo

  const canShowThemeCustomization = Boolean(
    realClientId && (themeVariants.length > 0 || extractedBranding)
  );

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
      {/* Top utility bar */}
      <div className="fixed top-3 right-3 z-50 flex items-center space-x-2">
        {/* Home button */}
        <button
          aria-label="Home"
          className={`p-2 rounded-lg shadow-sm border ${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-100'}`}
          onClick={() => (isConsultantOrSuper ? navigate('/consultant/dashboard') : navigate('/home'))}
        >
          <FontAwesomeIcon icon={faHome} className={theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} />
        </button>
        {/* Theme toggle */}
        <button
          aria-label="Toggle Theme"
          className={`p-2 rounded-lg shadow-sm border ${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-100'}`}
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          <FontAwesomeIcon icon={theme === 'dark' ? faMoon : faSun} className={theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} />
        </button>
        {realClientId && (
          <button
            aria-label={showWebsitePreview ? 'Hide website preview' : 'Show website preview'}
            className={`p-2 rounded-lg shadow-sm border transition-colors ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-200' : 'bg-white border-gray-200 hover:bg-gray-100 text-gray-700'
            }`}
            onClick={() => {
              if (showWebsitePreview) {
                setShowWebsitePreview(false);
              } else {
                setShowWebsitePreview(false);
                setTimeout(() => setShowWebsitePreview(true), 200);
              }
            }}
            title={showWebsitePreview ? 'Hide preview' : 'Preview widget'}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        )}
      </div>
      {/* Sidebar toggle positioned to the right of the left sidebar; moves with collapse */}
      <div className="fixed top-3 z-50" style={{ left: sidebarOpen ? 252 : 12 }}>
        <button
          aria-label="Toggle Sidebar"
          className={`p-2 rounded-lg shadow-sm border ${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-100'}`}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className={theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} />
        </button>
      </div>
      {/* Content Wrapper: Sidebar + Main Chat */}
      <div className="flex h-screen">
      {/* Sidebar */}
      {sidebarOpen && (
      <div className={`w-60 flex-shrink-0 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'} border-r flex flex-col overflow-hidden`}>
        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Conversations</h3>
              {conversations.length > 0 && (
                <button
                  onClick={() => {
                    try {
                      // Persist current conversation state only
                      saveCurrentConversationState();
                    } catch {}

                    if (currentStep === 'demo' && realClientId) {
                      // Always create exactly ONE fresh empty conversation
                        const newConvId = String(conversations.length);
                      setActiveConversationId(newConvId);

                      const freshState = createInitialConversationState();
                      freshState.realClientId = realClientId;
                      freshState.clientDisplayName = clientDisplayName;
                      freshState.websiteUrl = websiteUrl;
                      freshState.clientDetails = { ...clientDetails };
                      freshState.currentStep = 'demo';
                      freshState.waitingForInput = true;

                      restoreConversationState(freshState);

                      setConversations(prev => [...prev, {
                        id: newConvId,
                        clientId: realClientId,
                        title: 'New conversation',
                        messages: [],
                        created_at: new Date().toISOString(),
                        websiteUrl: websiteUrl,
                        clientName: '',
                        state: freshState
                      }]);
                    } else {
                      // Reset to beginning onboarding
                      setMessages([
                        {
                          role: 'assistant',
                          content: "👋 Hi! I'm here to show you how our AI assistant can transform your website into an intelligent helper for your visitors.\n\nLet's start by analyzing your website. What's your URL?",
                          timestamp: new Date()
                        }
                      ]);
                      setCurrentStep('website');
                      setWaitingForInput(true);
                      setSelectedPages(new Set());
                      setWebsiteUrl('');
                      setBotPurpose('');
                      setActiveQASessionId(null);
                      setActiveConversationId(null);
                      setSetupProgress(0);
                      setSetupPhaseLabel('');
                      setClientDisplayName('');
                      setPreScannedPages([]);
                      setClientDetails({
                        name: '',
                        email: '',
                        phone: '',
                        company_name: '',
                        password: ''
                      });
                      setRealClientId(null);
                      setHasCreatedInitialConversation(false);
                    }
                  }}
                  className={`p-1 rounded hover:bg-gray-700 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
                  title="New conversation"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                </button>
              )}
            </div>
            <div className="space-y-1">
              {conversations.length === 0 && (
                <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} italic py-2 text-center`}>
                  No conversations yet
                </div>
              )}
              {conversations.map((conv, idx) => {
                // Simplified title logic: Always prioritize client name
                const firstUserMsg = conv.messages.find(m => m.role === 'user' && m.content?.trim());
                let displayTitle = '';
                
                // 1. Use client name if available (highest priority)
                if (conv.clientName) {
                  displayTitle = conv.clientName;
                } 
                // 2. Use conversation title if it's meaningful (not default)
                else if (conv.title && conv.title !== 'Conversation' && conv.title !== 'New conversation') {
                  displayTitle = conv.title;
                } 
                // 3. For "New conversation" title, check if we can get client name from state
                else if (conv.title === 'New conversation' && conv.state?.clientDisplayName) {
                  displayTitle = conv.state.clientDisplayName;
                }
                // 4. Use first user message as fallback
                else if (firstUserMsg?.content) {
                  displayTitle = firstUserMsg.content.split('\n')[0].slice(0, 50);
                } 
                // 5. Final fallback
                else {
                  displayTitle = conv.title || `Conversation ${idx + 1}`;
                }
                
                return (
                  <div
                    key={conv.id}
                    className={`group flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                      activeConversationId === conv.id 
                        ? theme === 'dark' ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-900'
                        : theme === 'dark' ? 'hover:bg-gray-700/50 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        // Save current conversation state before switching
                        saveCurrentConversationState();
                        
                        // Switch to new conversation
                        setActiveConversationId(conv.id);
                        
                        // Restore complete conversation state
                        if (conv.state) {
                          // Restore from saved complete state
                          restoreConversationState(conv.state);
                        } else {
                          // Legacy conversation - restore basic state only
                          setMessages(conv.messages);
                          setCurrentStep(conv.messages.length > 1 ? 'demo' : 'website');
                          setWaitingForInput(true);
                          setClientDisplayName(conv.clientName || '');
                          setWebsiteUrl(conv.websiteUrl || '');
                          if (conv.clientId) {
                            setRealClientId(conv.clientId);
                          }
                          // Reset other state to defaults for legacy conversations
                          setConversationHistory([]);
                          setConversationId(null);
                          setClientDetails({
                            name: '',
                            email: '',
                            phone: '',
                            company_name: conv.clientName || '',
                            password: ''
                          });
                          setBotPurpose('');
                          setBotPersonality('');
                          setSelectedPages(new Set());
                          setPreScannedPages([]);
                          setQaCards([]);
                          setQaSessions([]);
                          setSessionId(null);
                          setActiveSessionId(null);
                          setCollectingField(null);
                          setSetupProgress(0);
                          setSetupPhaseLabel('');
                        }
                      }}
                    >
                      <div className="truncate font-medium">
                        {displayTitle}
                        {displayTitle && displayTitle.endsWith('...') ? '' : ''}
                      </div>
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        {new Date(conv.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const shouldDelete = window.confirm('Delete this conversation?');
                        if (shouldDelete) {
                          setConversations(prev => prev.filter(c => c.id !== conv.id));
                          if (activeConversationId === conv.id) {
                            // Reset to new conversation if deleting active one
                            setMessages([
                              {
                                role: 'assistant',
                                content: "👋 Hi! I'm here to show you how our AI assistant can transform your website into an intelligent helper for your visitors.\n\nLet's start by analyzing your website. What's your URL?",
                                timestamp: new Date()
                              }
                            ]);
                            setCurrentStep('website');
                            setActiveConversationId(null);
                            setClientDisplayName('');
                            setWebsiteUrl('');
                            setRealClientId(null);
                          }
                        }
                      }}
                      className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity ${
                        theme === 'dark' ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                      title="Delete conversation"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Progress Section (stick to bottom) - Only show during active setup/crawling */}
        {(currentStep !== 'demo' || messages.some(m =>
          m.crawlProgress &&
          ['crawling', 'embedding', 'extracting_contacts', 'extracting_branding', 'generating_themes'].includes(m.crawlProgress.status)
        )) && (
          <div className={`mt-auto p-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
              Setup Progress
            </div>
            <div className="relative">
              <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} h-2 rounded-full overflow-hidden`}>
                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out" style={{ width: `${setupProgress}%` }} />
              </div>
            </div>
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
              {(() => {
                // Get current crawl progress from messages if available
                const crawlMsg = messages.find(m => m.crawlProgress);
                if (crawlMsg?.crawlProgress?.status === 'crawling' && crawlMsg.crawlProgress.pagesProcessed !== undefined) {
                  return `Extracting contents... - ${crawlMsg.crawlProgress.pagesProcessed}/${crawlMsg.crawlProgress.totalPages} pages`;
                }
                
                return setupPhaseLabel || (
                  currentStep === 'website' ? 'Enter your website URL' : 
                  currentStep === 'scanning' ? 'Discovering your pages' : 
                  currentStep === 'bot-type' ? 'Choose AI purpose' : 
                  currentStep === 'personality' ? 'Choose AI personality' : 
                  currentStep === 'page-selection' ? 'Select pages to include' : 
                  currentStep === 'client-details' ? 'Your information' : 
                  currentStep === 'crawling' ? 'Building your AI' : 
                  `Try your ${clientDetails.company_name || 'AI assistant'}`
                );
              })()}
            </div>
          </div>
        )}
      </div>
      )}
      
      {/* Main Chat Area - Split view when preview is shown */}
      <div className={`flex-1 flex ${showWebsitePreview ? 'flex-row' : 'flex-col'} overflow-hidden`}>
        {/* Chat Section */}
        <div className={`${showWebsitePreview ? 'w-1/2' : 'flex-1'} flex flex-col overflow-hidden border-r ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>

        {/* Chat Messages */}
         <div className={`flex-1 overflow-y-auto modern-scroll ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="max-w-3xl mx-auto px-4 py-8">
            {messages.map((message, index) => (
              <div key={index} className="mb-6">
                {/* Regular message */}
                {message.role !== 'system' && (message.content || message.isThinking || message.isLoading) && (
                  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={message.role === 'user' ? 'max-w-[85%] order-1' : 'w-full'}>
                      {message.role === 'assistant' && (
                        <div className="flex items-start gap-3 mb-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
                          }`}>
                            <FontAwesomeIcon icon={faRobot} className="text-white text-sm" />
                          </div>
                          <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>{clientDetails.company_name || 'AI Assistant'}</span>
                        </div>
                      )}
                      <div className={`rounded-2xl px-5 py-3 ${
                        message.role === 'user' 
                          ? theme === 'dark' 
                            ? 'bg-gray-700 text-gray-100' 
                            : 'bg-gray-100 text-gray-900'
                          : theme === 'dark'
                            ? 'bg-transparent text-gray-100'
                            : 'bg-transparent text-white'
                      }`}>
                        {message.role === 'user' ? (
                          <div className="whitespace-pre-wrap">
                            {message.content}
                          </div>
                        ) : (
                          <div className={`message-response-display ${theme === 'dark' ? 'dark-theme' : ''}`}>
                            {(message.isThinking || message.isLoading || message.isStreaming) ? (
                              <div>
                                <MessageRenderer 
                                  content={message.content} 
                                  messageId={`message-${index}`}
                                  className={theme === 'dark' ? 'dark-theme' : ''}
                                />
                                {(message.isThinking || message.isLoading || (message.isStreaming && !message.content)) && (
                                  <div style={{display:'flex', alignItems:'center', gap:8, marginTop:10, color:'#6B7280', fontSize:13}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="animate-spin">
                                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                      <path d="m12 1 0 6m0 10 0 6m11-7-6 0m-10 0-6 0m8.5-8.5 4.24-4.24m-12.02 12.02 4.24-4.24m8.48 0 4.24 4.24m-12.02-12.02 4.24 4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    <span>{(() => {
                                      if (message.messageTag === 'qa-progress') {
                                        return 'Q&A in progress...';
                                      }
                                      // Show message.content directly for crawl-analyzing messages
                                      if (message.messageTag === 'crawl-analyzing') {
                                        // If content exists, show it; otherwise don't show fallback (content will come from SSE)
                                        return message.content || '';
                                      }
                                      // Prevent setup-status from showing loading if crawl-analyzing exists
                                      if (message.messageTag === 'setup-status') {
                                        const hasCrawlAnalyzing = messages.some(m => m.role === 'assistant' && m.messageTag === 'crawl-analyzing');
                                        if (hasCrawlAnalyzing) {
                                          return ''; // Don't show loading indicator for setup-status if crawl-analyzing exists
                                        }
                                      }
                                      // Fallback for other message types
                                      return currentStep === 'crawling' ? 'Processing...' : 'Generating response...';
                                    })()}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <MessageRenderer 
                                  content={message.content} 
                                  messageId={`message-${index}`}
                                  className={theme === 'dark' ? 'dark-theme' : ''}
                                />
                                {/* Feedback buttons - only for knowledge-based responses */}
                                {message.isKnowledgeResponse && !message.isLoading && !message.isStreaming && !message.isThinking && !message.isTyping && message.content && (
                                  <div className="feedback-buttons" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                    <button
                                      className={`feedback-btn thumbs-up ${message.feedback === 'thumbs_up' ? 'active' : ''}`}
                                      title="Helpful response"
                                      aria-label="thumbs up"
                                      onClick={() => handleFeedback(message.id || `message-${index}`, 'thumbs_up')}
                                      disabled={!!message.feedback}
                                      style={{ opacity: message.feedback && message.feedback !== 'thumbs_up' ? 0.5 : 1, cursor: message.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      👍
                                    </button>
                                    <button
                                      className={`feedback-btn thumbs-down ${message.feedback === 'thumbs_down' ? 'active' : ''}`}
                                      title="Not helpful"
                                      aria-label="thumbs down"
                                      onClick={() => handleFeedback(message.id || `message-${index}`, 'thumbs_down')}
                                      disabled={!!message.feedback}
                                      style={{ opacity: message.feedback && message.feedback !== 'thumbs_down' ? 0.5 : 1, cursor: message.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      👎
                                    </button>
                                    <button
                                      className={`feedback-btn need-review ${message.feedback === 'need_review' ? 'active' : ''}`}
                                      title="Needs admin attention"
                                      aria-label="need review"
                                      onClick={() => handleFeedback(message.id || `message-${index}`, 'need_review')}
                                      disabled={!!message.feedback}
                                      style={{ opacity: message.feedback && message.feedback !== 'need_review' ? 0.5 : 1, cursor: message.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      ⚠️
                                    </button>
                                  </div>
                                )}
                                
                                {/* Feedback reason selection for thumbs down */}
                                {showFeedbackReasonFor === (message.id || `message-${index}`) && (
                                  <div className="feedback-reason-tags" style={{ marginTop: '12px' }}>
                                    <span className="feedback-tags-label">What went wrong?</span>
                                    <div className="feedback-tags-container">
                                      {FEEDBACK_OPTIONS.map((option) => (
                                        <button
                                          key={option.value}
                                          className="feedback-tag"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleFeedback(message.id || `message-${index}`, 'thumbs_down', option.value);
                                          }}
                                        >
                                          {option.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Q&A Session Preview - inside message bubble */}
                                {message.qaCards && (
                                  <div className="mt-4">
                                    <QASessionPreview 
                                      qaCards={message.qaCards}
                                      messageId={message.id || `message-${index}`}
                                      onQAFeedback={handleQAFeedback}
                                      theme={theme}
                                      feedbackOptions={FEEDBACK_OPTIONS}
                                      showFeedbackReasonFor={showFeedbackReasonFor}
                                      onShowFeedbackReason={setShowFeedbackReasonFor}
                                      botName={clientDetails.company_name}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* System message: hide bot-recommendation badge */}
                {message.content && message.role === 'system' && message.content !== 'scan-complete' && message.content !== 'bot-recommendation' && (
                  <div className="flex justify-center my-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
                      theme === 'dark' 
                        ? 'bg-gray-800 text-gray-300' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
                      {message.content}
                    </span>
                  </div>
                )}
                
                {/* Scan Complete Message */}
                {message.role === 'system' && message.content === 'scan-complete' && message.scanComplete && (
                  <div className="flex justify-center my-4">
                    <div className={`rounded-xl p-6 max-w-2xl w-full ${
                      theme === 'dark'
                        ? 'bg-gray-800 border border-gray-700'
                        : 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200'
                    }`}>
                      <div className="text-center mb-4">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-3" />
                        <h3 className={`text-2xl font-bold ${
                          theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
                        }`}>
                          Found {message.scanComplete.totalFromSitemap || message.scanComplete.totalPages} pages{message.scanComplete.totalFromSitemap && message.scanComplete.totalFromSitemap > message.scanComplete.totalAnalyzed! ? (
                            <span className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {' '}(using top {message.scanComplete.totalAnalyzed} for demo)
                            </span>
                          ) : ''} across {message.scanComplete.categories.length} {message.scanComplete.categories.length === 1 ? 'category' : 'categories'}!
                        </h3>
                      </div>
                      
                      <div className="mt-6 max-h-80 overflow-y-auto pr-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {message.scanComplete.categories.map(({ category, count, icon }) => (
                            <div
                              key={category}
                              className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                                theme === 'dark'
                                  ? 'bg-gray-700/50 hover:bg-gray-700'
                                  : 'bg-white hover:bg-gray-50 border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{icon}</span>
                                <span className={`font-medium capitalize ${
                                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                }`}>
                                  {category}
                                </span>
                              </div>
                              <span className={`text-lg font-semibold ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                              }`}>
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bot Recommendation removed by request */}

                {/* Options */}
                {message.options && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                    {message.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={option.action}
                        disabled={isProcessing || option.disabled}
                        className={`border-2 rounded-xl p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          theme === 'dark'
                            ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-750 text-gray-100'
                            : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md text-gray-800'
                        }`}
                      >
                        <div className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
                          {option.label}
                        </div>
                        {option.description && (
                          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {option.description}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Page List */}
                {message.pageList && (
                  <div className="mt-4 max-w-2xl">
                    <div className={`rounded-xl border ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-gray-700' 
                        : 'bg-white border-gray-200'
                    }`}>
                      {/* Header */}
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <h4 className={`font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                          Select Pages ({(() => {
                            const normalizeUrl = (value: string) => value.replace(/\/$/, '').toLowerCase();
                            const baseHomeUrl = normalizeUrl(websiteUrl);

                            const isHomePage = (url: string) => {
                              const normalized = normalizeUrl(url);
                              const patterns = [
                                baseHomeUrl,
                                baseHomeUrl + '/',
                                baseHomeUrl + '/index.html',
                                baseHomeUrl + '/index.php',
                                baseHomeUrl + '/home',
                              ];
                              return patterns.some(pattern => normalized === pattern);
                            };

                            const homepage = message.pageList?.find(p => isHomePage(p.url));
                            const otherPages = message.pageList?.filter(p => !isHomePage(p.url)) || [];
                            const filteredOtherPages = pageSearchTerm
                              ? otherPages.filter(page =>
                                  page.title.toLowerCase().includes(pageSearchTerm) ||
                                  page.url.toLowerCase().includes(pageSearchTerm)
                                )
                              : otherPages;
                            const totalFiltered = homepage ? 1 + filteredOtherPages.length : filteredOtherPages.length;

                            return pageSearchTerm ? `${totalFiltered} of ${message.pageList?.length || 0}` : (message.pageList?.length || 0);
                          })()} found)
                          {isDemoMode && <span className="text-sm font-normal text-orange-600 dark:text-orange-400 ml-2">(Demo: max {maxPagesForDemo} pages)</span>}
                          {pageSearchTerm && <span className="text-sm font-normal text-blue-600 dark:text-blue-400 ml-2"> (filtered)</span>}
                        </h4>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Choose which pages to include in your AI assistant
                          {isDemoMode && <span className="block text-orange-600 dark:text-orange-400 mt-1">
                            Demo mode: Homepage is required + up to 9 additional pages (total 10)
                          </span>}
                        </p>

                        {/* Search Input */}
                        <div className="mt-3 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon
                              icon={faSearch}
                              className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Search pages by title or URL..."
                            value={pageSearchTerm}
                            className={`w-full pl-10 pr-10 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                                : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
                            }`}
                            onChange={(e) => setPageSearchTerm(e.target.value)}
                          />
                          {pageSearchTerm && (
                            <button
                              onClick={() => setPageSearchTerm('')}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Scrollable page list */}
                      <div className="max-h-80 overflow-y-auto">
                        <div className="p-2 space-y-1">
                          {(() => {
                            // Filter and sort pages based on search term
                            const normalizeUrl = (value: string) => value.replace(/\/$/, '').toLowerCase();
                            const baseHomeUrl = normalizeUrl(websiteUrl);

                            const isHomePage = (url: string) => {
                              const normalized = normalizeUrl(url);
                              const patterns = [
                                baseHomeUrl,
                                baseHomeUrl + '/',
                                baseHomeUrl + '/index.html',
                                baseHomeUrl + '/index.php',
                                baseHomeUrl + '/home',
                              ];
                              return patterns.some(pattern => normalized === pattern);
                            };

                            // Separate homepage and filter other pages
                            const homepage = message.pageList.find(p => isHomePage(p.url));
                            const otherPages = message.pageList.filter(p => !isHomePage(p.url));

                            // Filter other pages based on search term
                            const filteredOtherPages = pageSearchTerm
                              ? otherPages.filter(page =>
                                  page.title.toLowerCase().includes(pageSearchTerm) ||
                                  page.url.toLowerCase().includes(pageSearchTerm)
                                )
                              : otherPages;

                            // Combine homepage (always first) with filtered other pages
                            const sortedPages = homepage ? [homepage, ...filteredOtherPages] : filteredOtherPages;

                            return sortedPages.map((page, pageIndex) => {
                            const isHomePageResult = isHomePage(page.url);
                            const currentSelectionSize = selectedPagesRef.current.size;
                            const isDisabled = page.disabled || (!page.selected && currentSelectionSize >= maxPagesForDemo);

                            return (
                            <div
                              key={pageIndex}
                              onClick={() => !isDisabled && togglePageSelection(page.url)}
                              className={`flex items-start p-3 rounded-lg transition-all ${
                                isDisabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer hover:shadow-sm'
                              } ${
                                page.selected
                                  ? theme === 'dark'
                                    ? 'bg-blue-900/20 border border-blue-700/50'
                                    : 'bg-blue-50 border border-blue-200'
                                  : theme === 'dark'
                                    ? 'hover:bg-gray-700 border border-transparent'
                                    : 'hover:bg-gray-50 border border-transparent'
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={page.disabled ? faLock : (page.selected ? faSquareCheck : faSquare)}
                                className={`mr-3 mt-1 text-lg flex-shrink-0 ${page.disabled ? 'text-gray-500' : page.selected ? 'text-blue-500' : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                                  {page.title}
                                  {isHomePageResult && <span className="ml-2 text-xs text-blue-600 font-medium">🏠 Required</span>}
                                  {isDisabled && !page.disabled && <span className="ml-2 text-xs text-orange-600 font-medium">🔒 Limit Reached</span>}
                                </div>
                                <div className={`text-sm truncate mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {page.url}
                                </div>
                              </div>
                              {/* Category badge */}
                              <div className={`ml-2 px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                                theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {getCategoryIcon((page as any).category || 'general')}
                              </div>
                            </div>
                          );
                          })}
                          )()}
                        </div>
                      </div>
                      
                      {/* Fixed bottom actions */}
                      <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="text-sm mb-3 text-center">
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Selected: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                            {message.pageList.filter(p => p.selected).length} pages
                          </span>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => {
                              console.log('Select All button clicked');
                              toggleAllPages();
                            }}
                            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                              theme === 'dark'
                                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                          >
                            {(message.pageList?.filter(p => p.selected).length || 0) === (message.pageList?.length || 0)
                              ? '☐ Deselect All'
                              : isDemoMode ? '✅ Select Homepage + Top 9' : '☑ Select All'
                            }
                          </button>

                          <button
                            onClick={() => {
                              const selectedCount = message.pageList?.filter(p => p.selected).length || 0;
                              console.log('Confirm button clicked! Selected pages:', selectedCount);
                              confirmPageSelection(false);
                            }}
                            disabled={(message.pageList?.filter(p => p.selected).length || 0) === 0}
                            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              theme === 'dark'
                                ? 'bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-600'
                                : 'bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-400'
                            }`}
                          >
                            ✓ Confirm ({message.pageList?.filter(p => p.selected).length || 0})
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scanning Progress */}
                {message.scanProgress && (
                  <div className="w-full">
                    <div className={`rounded-xl border p-6 ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-gray-700' 
                        : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <FontAwesomeIcon icon={faGlobe} className="text-blue-500 text-2xl animate-spin-slow" />
                            <div className="absolute -top-1 -right-1">
                              <span className="flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                              </span>
                            </div>
                          </div>
                          <div>
                            <h3 className={`text-lg font-semibold ${
                              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                            }`}>
                              {message.scanProgress?.status === 'categorizing'
                                ? 'Categorizing pages with AI'
                                : 'Discovering pages'}
                            </h3>
                            <p className={`text-sm ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {message.scanComplete?.pagesDiscovered ?
                                `${message.scanComplete.pagesDiscovered.length} pages found` :
                                message.scanProgress?.pagesDiscovered ?
                                `${message.scanProgress.pagesDiscovered.length} pages found${
                                  message.scanProgress?.status === 'categorizing' ? ' • AI analyzing...' : ''
                                }` :
                                'Starting scan...'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {/* Progress bar */}
                        <div className={`rounded-full h-2 overflow-hidden ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-300"
                            style={{ width: `${message.scanProgress.progress}%` }}
                          />
                        </div>
                        
                        {/* Status message */}
                        <p className={`text-center text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {message.scanProgress.message}
                        </p>
                        
                        {/* Live page discovery feed */}
                        {((message.scanComplete?.pagesDiscovered && message.scanComplete.pagesDiscovered.length > 0) ||
                          (message.scanProgress?.pagesDiscovered && message.scanProgress.pagesDiscovered.length > 0)) && (
                          <div className={`mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto modern-scroll border rounded-lg p-3 ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                          }`}>
                            {((message.scanComplete?.pagesDiscovered || message.scanProgress?.pagesDiscovered) || []).slice(-10).map((page, idx) => (
                              <div key={idx} className={`flex items-center space-x-2 text-sm animate-fade-in p-2 rounded shadow-sm border ${
                                theme === 'dark' ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-200'
                              }`}>
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 flex-shrink-0" />
                                <a
                                  href={page.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`truncate flex-1 hover:underline transition-colors duration-200 ${
                                    theme === 'dark'
                                      ? 'text-blue-400 hover:text-blue-300'
                                      : 'text-blue-600 hover:text-blue-800'
                                  }`}
                                  title={`Visit: ${page.url}`}
                                >
                                  {page.title}
                                </a>
                                <span className={`text-sm ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  {getCategoryIcon(page.category)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Category breakdown */}
                        {message.scanProgress.categories && Object.keys(message.scanProgress.categories).length > 0 && (
                          <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                            <h4 className={`text-sm font-medium mb-3 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>Categories found:</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {Object.entries(message.scanProgress.categories).slice(0, 6).map(([cat, count]) => (
                                <div key={cat} className={`flex items-center space-x-2 p-2 rounded-lg ${
                                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                  <span className="text-xl">{getCategoryIcon(cat)}</span>
                                  <div>
                                    <div className={`text-sm capitalize ${
                                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                                    }`}>{cat}</div>
                                    <div className={`text-xs ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>{count} pages</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Crawl Progress - Hidden during crawling, only shown for legacy system messages if any */}
                {message.crawlProgress && message.role === 'system' && (
                  <div className="max-w-2xl mx-auto">
                    <div className={`text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="text-sm font-medium">
                        {message.crawlProgress.message}
                      </span>
                    </div>
                  </div>
                )}


                
                {/* Old Q&A Cards - Hidden */}
                {false && message.qaCards && (
                  <div className="mt-6 space-y-4">
                    {message.qaCards?.map((card, cardIndex) => (
                      <div key={cardIndex} className="space-y-3">
                        {/* User Question - Right aligned, clickable */}
                        <div className="flex justify-end">
                          <div className={`max-w-[85%]`}>
                            <div 
                              className={`rounded-2xl px-5 py-3 cursor-pointer transition-all ${
                                theme === 'dark' 
                                  ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' 
                                  : 'bg-gray-100 text-gray-900 hover:bg-gray-50'
                              }`}
                              onClick={() => {
                                // Set the question in input and trigger send
                                setInputValue(card.question);
                                setCurrentStep('demo');
                                setWaitingForInput(true);
                                // Optionally auto-send the question
                                setTimeout(() => {
                                  handleUserInput();
                                }, 100);
                              }}
                              title="Click to ask this question"
                            >
                              <div className="whitespace-pre-wrap">
                                {card.question}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* AI Answer - Left aligned */}
                        <div className="flex justify-start">
                          <div className="max-w-[85%]">
                            <div className="flex items-start gap-3 mb-1">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
                              }`}>
                                <FontAwesomeIcon icon={faRobot} className="text-white text-sm" />
                              </div>
                              <span className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>{clientDetails.company_name || 'AI Assistant'}</span>
                            </div>
                            <div className={`rounded-2xl px-5 py-3 ${
                              theme === 'dark'
                                ? 'bg-transparent text-gray-100'
                                : 'bg-transparent text-gray-900'
                            }`}>
                              <div className={`message-response-display ${theme === 'dark' ? 'dark-theme' : ''}`}>
                                <MessageRenderer 
                                  content={card.answer} 
                                  messageId={`qa-${index}-${cardIndex}`}
                                  className={theme === 'dark' ? 'dark-theme' : ''}
                                />
                              </div>
                              {card.source && (
                                <div className={`flex items-center text-xs mt-2 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                                  Source: {card.source}
                                </div>
                              )}
                              {/* Feedback buttons for Q&A */}
                              {card.id && (
                                <>
                                  <div className="feedback-buttons" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                    <button
                                      className={`feedback-btn thumbs-up ${card.feedback === 'thumbs_up' ? 'active' : ''}`}
                                      title="Helpful response"
                                      aria-label="thumbs up"
                                      onClick={() => handleQAFeedback(message.id || `message-${index}`, cardIndex, 'thumbs_up')}
                                      disabled={!!card.feedback}
                                      style={{ opacity: card.feedback && card.feedback !== 'thumbs_up' ? 0.5 : 1, cursor: card.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      👍
                                    </button>
                                    <button
                                      className={`feedback-btn thumbs-down ${card.feedback === 'thumbs_down' ? 'active' : ''}`}
                                      title="Not helpful"
                                      aria-label="thumbs down"
                                      onClick={() => handleQAFeedback(message.id || `message-${index}`, cardIndex, 'thumbs_down')}
                                      disabled={!!card.feedback}
                                      style={{ opacity: card.feedback && card.feedback !== 'thumbs_down' ? 0.5 : 1, cursor: card.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      👎
                                    </button>
                                    <button
                                      className={`feedback-btn need-review ${card.feedback === 'need_review' ? 'active' : ''}`}
                                      title="Needs admin attention"
                                      aria-label="need review"
                                      onClick={() => handleQAFeedback(message.id || `message-${index}`, cardIndex, 'need_review')}
                                      disabled={!!card.feedback}
                                      style={{ opacity: card.feedback && card.feedback !== 'need_review' ? 0.5 : 1, cursor: card.feedback ? 'not-allowed' : 'pointer' }}
                                    >
                                      ⚠️
                                    </button>
                                  </div>
                                  
                                  {/* Feedback reason selection for Q&A thumbs down */}
                                  {showFeedbackReasonFor === `qa-${message.id || `message-${index}`}-${cardIndex}` && (
                                    <div className="feedback-reason-tags" style={{ marginTop: '12px' }}>
                                      <span className="feedback-tags-label">What went wrong?</span>
                                      <div className="feedback-tags-container">
                                        {FEEDBACK_OPTIONS.map((option) => (
                                          <button
                                            key={option.value}
                                            className="feedback-tag"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              handleQAFeedback(message.id || `message-${index}`, cardIndex, 'thumbs_down', option.value);
                                            }}
                                          >
                                            {option.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Client Details Form - No longer used, handled conversationally */}
                {/* Removed client details form */}
                {false && (
                  <div className="mt-6 max-w-md mx-auto">
                    <div className={`rounded-2xl p-6 border ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                    }`}>
                      <h3 className={`text-xl font-bold mb-4 text-center ${
                        theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
                      }`}>
                        Your Information
                      </h3>
                      
                      <form onSubmit={handleClientDetailsSubmit} className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                            Your Name
                          </label>
                          <input
                            type="text"
                            value={clientDetails.name}
                            onChange={(e) => setClientDetails({...clientDetails, name: e.target.value})}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <FontAwesomeIcon icon={faBuilding} className="mr-2" />
                            Company Name
                          </label>
                          <input
                            type="text"
                            value={clientDetails.company_name}
                            onChange={(e) => setClientDetails({...clientDetails, company_name: e.target.value})}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="ACME Corp"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={clientDetails.email}
                            onChange={(e) => setClientDetails({...clientDetails, email: e.target.value})}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="john@acme.com"
                            required
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <FontAwesomeIcon icon={faPhone} className="mr-2" />
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={clientDetails.phone}
                            onChange={(e) => setClientDetails({...clientDetails, phone: e.target.value})}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="+1 (555) 123-4567"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <FontAwesomeIcon icon={faKey} className="mr-2" />
                            Password
                          </label>
                          <div className="relative">
                            <input
                              type={passwordVisible ? "text" : "password"}
                              value={clientDetails.password}
                              onChange={(e) => setClientDetails({...clientDetails, password: e.target.value})}
                              className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                theme === 'dark'
                                  ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                              }`}
                              placeholder="Min 8 characters"
                              required
                              minLength={8}
                            />
                            <button
                              type="button"
                              onClick={() => setPasswordVisible(!passwordVisible)}
                              className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                                theme === 'dark'
                                  ? 'text-gray-400 hover:text-gray-200'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
                            </button>
                          </div>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isProcessing}
                          className={`w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            theme === 'dark'
                              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                              : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                          }`}
                        >
                          {isProcessing ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Creating Your Account...
                            </>
                          ) : (
                            <>
                              Create Account & Start Building
                              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                )}

              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area - Claude Style */}
        <div className={`border-t ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="max-w-3xl mx-auto px-4 py-4">
            <form onSubmit={(e) => { e.preventDefault(); handleUserInput(); }} className="relative">
              <textarea
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleUserInput();
                  }
                }}
                placeholder={
                  currentStep === 'website' ? "Enter your website URL (e.g., example.com)" :
                  currentStep === 'client-details' && collectingField === 'bot_name' ? "Enter bot name (e.g., SecureSet AI)" :
                  currentStep === 'client-details' && collectingField === 'email' ? "Enter your email address" :
                  currentStep === 'client-details' && collectingField === 'name' ? "Enter your name" :
                  currentStep === 'client-details' && collectingField === 'phone' ? "Enter your phone number" :
                  currentStep === 'client-details' ? "Type your response or provide: name, email, phone" :
                  currentStep === 'demo' ? "Ask me anything about your business..." :
                  "Type your response..."
                }
                className={`w-full px-4 py-3 pr-12 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                } border`}
                disabled={isProcessing || !waitingForInput}
                rows={1}
                style={{ minHeight: '52px', maxHeight: '200px' }}
              />
              <button
                type="submit"
                disabled={isProcessing || !inputValue.trim() || !waitingForInput}
                className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {isProcessing ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin w-5 h-5" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </form>
            {!waitingForInput && !isProcessing && (
              <p className={`text-xs mt-2 text-center ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                Please wait for the assistant to respond...
              </p>
            )}
          </div>
        </div>

        </div>

        {/* Website Preview Panel - Right Side */}
        {showWebsitePreview && (
          <div className={`w-1/2 flex flex-col overflow-hidden relative ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`} style={{ zIndex: 100 }}>
            <WebsitePreview
              key={`preview-${realClientId}-${enhancedBranding ? 'branded' : 'default'}-${ecommerceAssistantEnabled}`}
              clientId={realClientId || 'demo'}
              websiteUrl={websiteUrl}
              theme={theme}
              extractedBranding={enhancedBranding}
              onClose={() => setShowWebsitePreview(false)}
              onEcommerceAssistantToggle={setEcommerceAssistantEnabled}
              ecommerceAssistantEnabled={ecommerceAssistantEnabled}
              botType={botType}
              onThemeClick={canShowThemeCustomization ? async () => {
                if (themeVariants.length === 0 && extractedBranding && realClientId) {
                  // First try to fetch existing variants, then generate if none exist
                  const variantsExist = await fetchThemeVariants(realClientId);
                  if (!variantsExist) {
                    await generateThemeVariants(realClientId);
                  }
                  // Automatically show selector after generating variants
                  setShowThemeSelector(true);
                } else {
                  // Toggle selector if variants already exist
                  setShowThemeSelector(!showThemeSelector);
                }
              } : undefined}
              hasThemeVariants={themeVariants.length > 0}
              isFetchingVariants={isFetchingVariants}
              isGeneratingVariants={isGeneratingVariants}
            />
          </div>
        )}
      </div>
      
      {/* Floating Preview Toggle Button - Shows when AI is trained */}
      {realClientId && waitingForInput && !showWebsitePreview && (
        <button
          onClick={() => {
            setShowWebsitePreview(false);
            // Longer delay to ensure branding is applied and widget refreshes
            setTimeout(() => {
              console.log('Showing preview after branding delay (floating button)');
              setShowWebsitePreview(true);
            }, 1000);
          }}
          className="fixed bottom-8 right-8 mr-4 px-4 py-2 hover:bg-blue-50 rounded-xl flex items-center text-[#065BDF] transition-all duration-200 border border-blue-100 hover:border-blue-200 shadow-sm hover:shadow-md bg-white"
          title="Preview how widget looks on your website"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="fas"
              data-icon="robot"
              className="text-white text-sm"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 512"
            >
              <path
                fill="currentColor"
                d="M320 0c17.7 0 32 14.3 32 32l0 64 120 0c39.8 0 72 32.2 72 72l0 272c0 39.8-32.2 72-72 72l-304 0c-39.8 0-72-32.2-72-72l0-272c0-39.8 32.2-72 72-72l120 0 0-64c0-17.7 14.3-32 32-32zM208 384c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zM264 256a40 40 0 1 0 -80 0 40 40 0 1 0 80 0zm152 40a40 40 0 1 0 0-80 40 40 0 1 0 0 80zM48 224l16 0 0 192-16 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm544 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-16 0 0-192 16 0z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="font-semibold text-sm">AI Assistant</div>
            <div className="text-xs text-blue-500">Ask me anything</div>
          </div>
        </button>
      )}
      
      {/* Theme Customization Button - Shows when preview is open and variants are available */}
      {(() => {
        console.log('Theme button conditions:', {
          realClientId,
          showWebsitePreview,
          themeVariantsLength: themeVariants.length,
          themeVariants,
          extractedBranding,
          qaCardsLength: qaCards.length,
          canShowThemeCustomization
        });
        return null;
      })()}
      
      {/* Theme Selector Modal */}
      {showThemeSelector && themeVariants.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
          <div className={`max-w-5xl w-full rounded-2xl shadow-2xl overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header */}
            <div className={`px-8 py-6 border-b ${
              theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {isGeneratingVariants
                      ? 'Creating Theme Variants'
                      : themeVariants[0]?.type === 'ecommerce_widget'
                        ? 'Choose Your Voice Search Widget Theme'
                        : 'Choose Your Chat Widget Theme'}
                  </h2>
                  <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isGeneratingVariants
                      ? 'Generating beautiful theme variants based on your brand colors...'
                      : themeVariants[0]?.type === 'ecommerce_widget'
                        ? 'Select a theme for your voice search widget and product results overlay. Changes apply instantly.'
                        : 'Select a theme variant that best matches your brand. Changes apply instantly to the preview.'
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowThemeSelector(false)}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark' 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                      : 'hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
            </div>
            
            {/* Theme Variants */}
            <div className="p-8">
              {isGeneratingVariants ? (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faSpinner} className="text-4xl animate-spin mb-4 text-blue-500" />
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                    Generating theme variants based on your brand...
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6">
                  {themeVariants.map((variant, index) => (
                    <div
                      key={index}
                      className={`relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                        selectedVariant === index
                          ? 'ring-4 ring-blue-500 transform scale-105'
                          : 'hover:transform hover:scale-[1.02]'
                      }`}
                      onClick={() => {
                        console.log('Theme variant clicked:', index);
                        applyThemeVariant(index);
                      }}
                    >
                      {/* Selected Badge */}
                      {selectedVariant === index && (
                        <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                          Active
                        </div>
                      )}

                      {/* Theme Preview */}
                      <div className={`p-5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <h3 className={`text-sm font-semibold mb-3 tracking-wide ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                          {variant.name || `Theme ${index + 1}`}
                        </h3>

                        {(() => {
                          // Check if this is an e-commerce widget variant
                          if (variant.type === 'ecommerce_widget') {
                            // E-commerce widget preview - matching your exact screenshot
                            const widgetBg = variant.mapping?.voiceWidgetBgColor || variant.mapping?.primaryColor || '#cb9a51';
                            const widgetIcon = variant.mapping?.voiceWidgetIconColor || '#ffffff';
                            const overlayBg = variant.mapping?.voiceResultsOverlayBg || variant.mapping?.overlayBgColor || '#ffffff';
                            const overlayText = variant.mapping?.voiceResultsTextColor || variant.mapping?.overlayTextColor || '#1f2937';
                            const productCardBg = variant.mapping?.voiceResultsCardBg || variant.mapping?.productCardBg || '#ffffff';
                            const productCardBorder = variant.mapping?.voiceResultsCardBorder || variant.mapping?.productCardBorder || '#e5e7eb';
                            const productTitleColor = variant.mapping?.productTitleColor || overlayText;
                            const priceColor = variant.mapping?.voiceResultsPriceColor || variant.mapping?.priceColor || variant.mapping?.primaryColor || '#cb9a51';
                            const buttonBg = variant.mapping?.voiceResultsButtonBg || variant.mapping?.buttonBgColor || variant.mapping?.primaryColor || '#cb9a51';
                            const buttonText = variant.mapping?.voiceResultsButtonText || variant.mapping?.buttonTextColor || '#ffffff';

                            return (
                              <div className="relative h-[140px]">
                                {/* Product Search Overlay - Clean and simple like the screenshot */}
                                <div
                                  className="rounded-lg overflow-hidden h-full"
                                  style={{
                                    background: overlayBg,
                                    border: `1px solid ${productCardBorder}`,
                                  }}
                                >
                                  {/* Product Grid */}
                                  <div className="p-2 h-full">
                                    <div className="grid grid-cols-2 gap-2 h-full">
                                      {[1, 2].map((i) => (
                                        <div
                                          key={i}
                                          className="flex flex-col"
                                          style={{
                                            background: productCardBg,
                                            border: `1px solid ${productCardBorder}`,
                                            borderRadius: '6px',
                                            padding: '8px'
                                          }}
                                        >
                                          {/* Product Image Placeholder */}
                                          <div
                                            className="w-full aspect-square mb-2 rounded"
                                            style={{
                                              background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)'
                                            }}
                                          />
                                          {/* Product Name */}
                                          <div className="text-[9px] font-medium mb-1" style={{ color: productTitleColor }}>
                                            Product Name {i}
                                          </div>
                                          {/* Price */}
                                          <div className="text-[10px] font-bold mb-2" style={{ color: priceColor }}>
                                            $99.99
                                          </div>
                                          {/* View Details Button */}
                                          <button
                                            className="w-full py-1 rounded text-[8px] font-medium mt-auto"
                                            style={{
                                              background: buttonBg,
                                              color: buttonText
                                            }}
                                          >
                                            View Details
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Bottom Action Bar */}
                                  <div
                                    className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2 py-1.5"
                                    style={{
                                      background: overlayBg,
                                      borderTop: `1px solid ${productCardBorder}`
                                    }}
                                  >
                                    {/* Search Products Button */}
                                    <button
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                                      style={{
                                        background: buttonBg,
                                        color: buttonText,
                                        fontSize: '8px'
                                      }}
                                    >
                                      <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                      </svg>
                                      <span>Search Products</span>
                                    </button>

                                    {/* Search Again */}
                                    {/* <button
                                      className="flex items-center gap-0.5"
                                      style={{
                                        color: overlayText,
                                        fontSize: '8px'
                                      }}
                                    >
                                      <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                                      </svg>
                                      <span>SEARCH AGAIN</span>
                                    </button> */}
                                  </div>
                                </div>

                                {/* Voice Widget Trigger - Bottom Left like in screenshot */}
                                <div className="absolute -bottom-1 -left-1 z-10">
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center shadow-md"
                                    style={{
                                      background: widgetBg,
                                      border: '2px solid white'
                                    }}
                                  >
                                    <svg width="18" height="18" fill={widgetIcon} viewBox="0 0 24 24">
                                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // Regular chat interface preview (existing code)
                          const gradientStart = variant.mapping?.gradientStartColor || variant.mapping?.chatContainerGradientStart;
                          const gradientEnd = variant.mapping?.gradientEndColor || variant.mapping?.chatContainerGradientEnd;
                          const chatBackground = variant.mapping?.chatContainerGradient
                            || (gradientStart && gradientEnd ? `linear-gradient(140deg, ${gradientStart} 0%, ${gradientEnd} 100%)`
                            : variant.mapping?.chatContainerBg || '#f4f4f5');
                          const headerBackground = variant.mapping?.headerBg
                            || `linear-gradient(135deg, ${variant.mapping?.primaryColor || '#2563eb'} 0%, ${variant.mapping?.secondaryColor || variant.mapping?.primaryColor || '#2563eb'} 100%)`;
                          const assistantBg = variant.mapping?.messageResponseBg || '#ffffff';
                          const userBg = variant.mapping?.sendButtonBg || variant.mapping?.buttonBgColor || variant.mapping?.primaryColor || '#2563eb';
                          const borderColor = variant.mapping?.borderColor || 'rgba(0,0,0,0.06)';
                          const inputBg = variant.mapping?.inputFieldBg || '#ffffff';
                          const textColor = variant.mapping?.chatTextColor || '#1f2937';
                          const secondaryText = variant.mapping?.inputTextColor || '#6b7280';

                          return (
                            <div className="rounded-xl overflow-hidden shadow-lg" style={{ width: 220, background: chatBackground }}>
                              <div className="px-3 py-2 flex items-center justify-between" style={{ background: headerBackground, color: variant.mapping?.headerTextColor || '#ffffff' }}>
                                <div className="flex items-center gap-2 text-xs">
                                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{
                                    background: variant.mapping?.floatingAvatarBgColor || variant.mapping?.primaryColor || '#1d4ed8',
                                    color: variant.mapping?.floatingAvatarIconColor || '#ffffff'
                                  }}>
                                    AI
                                  </div>
                                  <span className="font-semibold truncate max-w-[90px]">
                                    {clientDetails.company_name || 'AI Assistant'}
                                  </span>
                                </div>
                                <span className="text-[10px] opacity-80">Live</span>
                              </div>

                              <div className="px-3 py-3 space-y-2" style={{ background: chatBackground }}>
                                <div className="max-w-[85%] rounded-lg px-3 py-2 text-[11px] leading-tight" style={{
                                  background: assistantBg,
                                  color: textColor,
                                  border: `1px solid ${borderColor}`
                                }}>
                                  Hi there! Ask anything about your business.
                                </div>
                                <div className="ml-auto max-w-[85%] rounded-lg px-3 py-2 text-[11px] leading-tight shadow-sm" style={{
                                  background: userBg,
                                  color: variant.mapping?.buttonTextColor || '#ffffff'
                                }}>
                                  Tell me about your services
                                </div>
                              </div>

                              <div className="px-3 py-2 border-t flex items-center gap-2" style={{ borderColor, background: inputBg }}>
                                <div className="flex-1 text-[10px] px-2 py-1.5 rounded border" style={{
                                  borderColor,
                                  color: secondaryText,
                                  background: inputBg
                                }}>
                                  Type a message...
                                </div>
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px]" style={{
                                  background: userBg,
                                  color: variant.mapping?.sendButtonTextColor || variant.mapping?.buttonTextColor || '#ffffff'
                                }}>
                                  ➤
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Description */}
              <div className={`mt-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <p>These theme variants are generated based on your brand colors extracted from {websiteUrl}</p>
                <p className="mt-2">
                  {themeVariants[0]?.type === 'ecommerce_widget'
                    ? 'The selected theme will be applied to your voice search widget and product overlay.'
                    : 'The selected theme will be applied to your chat widget in real-time.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default OnboardingDemo;
